/**
 * V2 Bootstrap Bundle - Gladly API
 *
 * This is the lightweight bootstrap bundle that:
 * 1. Creates the Gladly object with all public API methods
 * 2. Handles init() which loads config and shows either the bootstrap input UI
 *    or the chat icon button (if previously closed)
 * 3. Loads the full widget bundle on-demand when user submits a message,
 *    clicks the chat icon, or has persisted credentials
 *
 * This bundle does NOT import the Zustand store to keep the bundle small.
 * User credentials are read directly from localStorage when needed.
 * The full store is only available after startFullWidget calls _setStore().
 */

import { beaconToolCallSettled, beaconToolStatusReceived } from '@/actions/messages/agentActivity';
import { resetConversation, resetConversationLocally } from '@/actions/session/resetConversation';
import { enterTestMode } from '@/actions/testMode/enterTestMode';
import { exitTestMode } from '@/actions/testMode/exitTestMode';
import { restartTestConversation } from '@/actions/testMode/restartTestConversation';
import { applyConfigurationOverride } from '@/configuration/applyConfigurationOverride';
import fetchAppConfiguration from '@/configuration/fetchAppConfiguration';
import { resolveChatTransportDecision } from '@/configuration/resolveChatTransportDecision';
import type { ActionEnvironment } from '@/domain/actionEnvironment';
import type { WidgetState, WidgetTransitionSource } from '@/domain/analytics';
import { WIDGET_STATES } from '@/domain/analytics';
import type { DynamicConfiguration, InitConfiguration, ServerConfiguration } from '@/domain/configuration';
import { ALIGNMENTS } from '@/domain/configuration';
import { borderRadiusToPx } from '@/domain/configurationV2';
import {
  DEFAULT_ON_PAGE_ACCENT,
  DEFAULT_ON_PAGE_BORDER_RADIUS_PX,
  DEFAULT_V2_FONT,
  DEFAULT_Z_INDEX,
} from '@/domain/defaultV2Configuration';
import type { DeviceConfig } from '@/domain/deviceConfig';
import { PLATFORMS } from '@/domain/deviceConfig';
import type { ChatTransportDecision, TestChannelSlot } from '@/domain/gateway';
import { isButtonLauncherForced, resolveLauncherButton } from '@/domain/launcherButton';
import { getLegacyUITheme, isLegacyUIEnabled } from '@/domain/legacyUiConfig';
import type { TestModeState, TestModeStatus, TestModeSupport } from '@/domain/testMode';
import { isTestingWith, TESTER_PROTOCOL_VERSION, toTestModeStatus } from '@/domain/testMode';
import { DisplayMode } from '@/domain/ui';
import type { PublicUser } from '@/domain/user';
import { hasUser, hasUserCredentials, UserIdentityType, UserType } from '@/domain/user';
import { sanitizeLaunchers } from '@/domain/validators/validServerConfiguration';
import { toRehydratedTestModeState, validTestModeConfig } from '@/domain/validators/validTestModeState';
import type { OpenWidgetAndSend } from '@/launchers/openWidgetAndSend';
import { createOpenWidgetAndSend } from '@/launchers/openWidgetAndSend';
import { startLaunchers, stopLaunchers, syncLaunchersToUrl } from '@/launchers/orchestrator';
import { ApiEventEmitter } from '@/lib/apiEventEmitter';
import { onEngagementRecorded, readLastEngagedAt } from '@/lib/attribution/engagementMarker';
import { loadOrderCapture } from '@/lib/attribution/loadOrderCapture';
import {
  isLiveEngagement,
  isOrderCaptureOnLoadEnabled,
  isWithinCaptureWindow,
} from '@/lib/attribution/orderCapturePolicy';
import type { OrderCapture } from '@/lib/attribution/startOrderCapture';
import {
  beaconWidgetOpened,
  configureBeaconTabLock,
  coolBeaconConnection,
  destroyBeaconGateway,
  rearmBeaconConnection,
  registerBeaconGatewayFactory,
  warmBeaconConnection,
} from '@/lib/beacon/beaconConnection';
import { loadPageViews } from '@/lib/beacon/loadPageViews';
import { answerFor, createPagePromptsHolder } from '@/lib/beacon/pagePrompts';
import type { PageViewTracker } from '@/lib/beacon/pageView';
import type { BootstrapAnalytics } from '@/lib/bootstrapAnalytics';
import { createBootstrapAnalytics } from '@/lib/bootstrapAnalytics';
import { getBootstrapClosedState, setBootstrapClosedState } from '@/lib/bootstrapState';
import {
  type DisplayedChatBarPlaceholders,
  displayedChatBarPlaceholders,
  type PlaceholderList,
  resolveChatBarPlaceholders,
  samePlaceholders,
} from '@/lib/chatBar/chatBarPlaceholders';
import { ClientIdManager } from '@/lib/clientIdManager';
import { accentGradientStops, colorToRgba, readableColor } from '@/lib/colorUtils';
import { createTooltip } from '@/lib/createTooltip';
import {
  applyCustomButtonState,
  hideCustomButton,
  isCustomButtonActive,
  isCustomButtonPresent,
  watchCustomButton,
} from '@/lib/customButton';
import { CHAT_BAR_DISPLAY, type ChatBarDisplay } from '@/lib/displayRules/evaluateUrlDisplayRules';
import { isChatBarVisible } from '@/lib/displayRules/isChatBarVisible';
import { createGoogleAnalyticsGateway } from '@/lib/googleAnalytics';
import { applyHostZIndex, HOST_CONTAINER_ID, isWidgetMounted, setHostLandmark } from '@/lib/hostContainer';
import { HOST_RESET_CSS } from '@/lib/hostResetCss';
import { isMobileWidth } from '@/lib/isMobileWidth';
import isValidEmail from '@/lib/isValidEmail';
import { enableLogging, logger } from '@/lib/logger';
import { throttle } from '@/lib/utils/throttle';
import { getHasActiveConversation, getIsAllowedByDisplayRules } from '@/selectors/widget';
import { getPersistedState, setAppIdForStorage } from '@/stores/persistedStorage';
import type { WidgetStoreApi } from '@/stores/storeTypes';

// Type declarations for window extensions
interface WindowWithGladly extends Window {
  Gladly?: unknown;
}

export interface WidgetInfo {
  featureFlags: string[];
  version: 2;
}

export interface WidgetStatus {
  hasActiveConversation: boolean | undefined;
  isInitialized: boolean;
  isWidgetLoaded: boolean;
  isWidgetOpen: boolean;
}

const WIDGET_VERSION = 2;

// Event emitter singleton - lives in bootstrap scope so on() works before full widget loads
const emitter = new ApiEventEmitter();
let nextObserverId = 0;

// Store API - will be set by startFullWidget via _setStore()
// We don't import the store here to keep the bootstrap bundle small.
// This holds the Zustand store API (with getState()) so we always read live state.
let _storeApi: WidgetStoreApi | null = null;

// Action environment - will be set by startFullWidget via _setActionEnvironment()
// Needed for API methods that require gateways or analytics (show, close, clearUser, etc.)
let _actionEnv: ActionEnvironment | null = null;

// Latest status received before the chat session is logged in; replayed by
// startFullWidget unless stale.
let _pendingToolStatus: { callId: string; status: string; receivedAt: number } | null = null;
const PENDING_TOOL_STATUS_MAX_AGE_MS = 15_000;

function showToolStatus(status: string, callId: string): void {
  if (_actionEnv?.gateways.chatGateway.getSessionId()) {
    // A held status is older than this one, so a later replay must not show it.
    _pendingToolStatus = null;
    _actionEnv.runAction(beaconToolStatusReceived, { callId, status });
    return;
  }
  _pendingToolStatus = { callId, status, receivedAt: Date.now() };
}

// Bumped per gateway, so a tool call still finishing on a destroyed gateway
// cannot mark the status of the same call redelivered to its replacement.
let _beaconGatewayGeneration = 0;

function settleToolStatus(callId: string): void {
  // A held status whose tool has already finished has nothing left to describe.
  if (_pendingToolStatus?.callId === callId) {
    _pendingToolStatus = null;
    return;
  }
  _actionEnv?.runAction(beaconToolCallSettled, { callId });
}

let _initConfig: InitConfiguration | null = null;
let _deviceConfig: DeviceConfig = { platform: PLATFORMS.WEB };
/**
 * Which chat transport this page load boots against, resolved once in init().
 *
 * Cached rather than read at widget-load time because the two moments are far
 * apart — init() runs on page load, the widget bundle is fetched when something
 * opens the chat — and the persisted slice is writable in between, by the host
 * page and by the tester's own exit. Resolving late would let a widget
 * authenticate against one transport and build the other.
 */
let _transportDecision: ChatTransportDecision = 'production';
// The driver's port for this page load, and the gateway consumer waiting for
// it. Either arrives first.
let _testChannelPort: MessagePort | null = null;
let _testChannelConsumer: ((port: MessagePort) => void) | null = null;
const _testChannelSlot: TestChannelSlot = {
  take(consumer) {
    _testChannelConsumer = consumer;
    if (_testChannelPort) {
      consumer(_testChannelPort);
    }
  },
};
let _serverConfig: ServerConfiguration | null = null;
let _dynamicConfig: DynamicConfiguration | null = null;
/**
 * Whether `init()` has produced a usable configuration.
 *
 * Separate from `_initConfig` being set, which happens before the configuration
 * is fetched and before the `v2Enabled` gate that can abort init: without this,
 * a widget whose init threw still answered the Test Mode API as though it were
 * ready. Reset at the top of every init so a re-init cannot report the previous
 * run's state.
 */
let _initComplete = false;
let _isWidgetLoaded = false;
let _widgetLoadPromise: Promise<void> | null = null;
let _bootstrapAnalytics: BootstrapAnalytics | null = null;
let _pageViews: PageViewTracker | null = null;
let _pageViewRun = 0;
let _stopOrderCaptureWait: (() => void) | null = null;
export const _pagePrompts = createPagePromptsHolder();
// Scoped to the load in flight: see trackEntryPointOpen.
let _entryPointOpenReported = false;
let _clientIdManager: ClientIdManager | null = null;

// The armed bootstrap UI's cleanup, held on globalThis rather than in module
// scope. Several module instances share one document: `vi.resetModules()` builds
// a new instance while the old one's 1s scan interval keeps running, and a page
// that loads the bootstrap twice does the same. A module-scoped handle can only
// reach its own instance, so a discarded instance scans forever and throws once
// the environment is gone — the failure sc-261378 describes. One UI per document
// was already the invariant; this enforces it at document scope.
declare global {
  interface Window {
    __gladlyBootstrapUiCleanups?: Set<() => void>;
  }
}

function bootstrapUiCleanups(): Set<() => void> {
  window.__gladlyBootstrapUiCleanups ??= new Set<() => void>();
  return window.__gladlyBootstrapUiCleanups;
}

// The page's order capture, held on window for the same reason: a second module
// instance must find the capture the first one started, not start its own beside
// it. `key` is the configuration it started with, so a re-init that asks for the
// same capture keeps it. Disposing it and starting again would publish an order
// already on the page a second time.
interface DocumentOrderCapture {
  /** The running capture; null while its chunk loads, or when there was nothing to capture with. */
  capture: OrderCapture | null;
  key: string;
  /** Set when a teardown or a different configuration replaces it while its chunk still loads. */
  stopped: boolean;
}

declare global {
  interface Window {
    __gladlyOrderCapture?: DocumentOrderCapture;
  }
}

// The submit tears the Chat Bar down before awaiting the load, so a failed load
// has nowhere else to read the shopper's text back from.
let _submittedChatBarText = '';

// The bootstrap UI's currently-rendered display-rule verdict, so navigate() only
// tears down / re-renders on an actual transition. Re-rendering on every SPA route
// change would drop the composer's text + focus while a shopper is mid-typing.
// The whole verdict rather than a visible flag: a bar -> button route change keeps
// an entry point on screen but has to swap which one, so latching on visibility
// alone would leave the bar up on a page that asked for the button.
// null = not yet evaluated (fresh init).
let _bootstrapUiDisplay: ChatBarDisplay | null = null;

// A pending delayed render (matched rule's displayDelay > 0), tracked so it can
// be cancelled if the page's visibility changes before it fires.
let _bootstrapDelayTimeout: ReturnType<typeof setTimeout> | null = null;

// Switches between our own launcher and a customer's. Not tied to
// teardownBootstrapUi, which also runs when merely swapping one bootstrap UI for
// another; cancelled when the widget load starts instead.
let _cancelSwitchWatch: (() => void) | null = null;

// Reveals a returning shopper's button during the widget-chunk load, so it
// outlives the bootstrap teardown loadFullWidget does on its way into that
// window. Cancelled once the widget has loaded.
let _cancelDirectLoadWatch: (() => void) | null = null;

function stopSwitchWatch(): void {
  _cancelSwitchWatch?.();
  _cancelSwitchWatch = null;
}

// The widget tier subscribes for itself once it is up.
function stopDirectLoadWatch(): void {
  _cancelDirectLoadWatch?.();
  _cancelDirectLoadWatch = null;
}

/** Exported for tests: several module instances share one document, and a watch
 * outliving its module would answer with a previous test's config. */
export function _stopCustomButtonWatch(): void {
  _cancelSwitchWatch?.();
  _cancelSwitchWatch = null;
  _cancelDirectLoadWatch?.();
  _cancelDirectLoadWatch = null;
}

/** Exported for tests: the rendered UI holds a 1s scan interval that only its teardown
 * clears, and an interval outliving the environment throws once `window` is gone. */
export function _teardownBootstrapUiForTest(): void {
  teardownBootstrapUi();
}

/** Exported for tests: true while any rendered bootstrap UI still holds its scan
 * interval, including one armed by a module instance this file has since
 * discarded. A test file that renders the UI asserts this is false once it has
 * finished, so a missing teardown fails that file instead of throwing after the
 * environment is gone and failing whichever file happened to run last. */
export function _hasBootstrapUiForTest(): boolean {
  return bootstrapUiCleanups().size > 0;
}

function clearBootstrapDelayTimeout(): void {
  if (_bootstrapDelayTimeout) {
    clearTimeout(_bootstrapDelayTimeout);
    _bootstrapDelayTimeout = null;
  }
}

function registerBootstrapUiTeardown(teardown: () => void): void {
  teardownBootstrapUi();
  // Closes over this instance's clearBootstrapDelayTimeout, so a later instance
  // running this cleanup also cancels this one's pending delayed render. That
  // state is module-scoped and unreachable from anywhere else.
  bootstrapUiCleanups().add(() => {
    clearBootstrapDelayTimeout();
    teardown();
  });
}

function teardownBootstrapUi(): void {
  clearBootstrapDelayTimeout();
  const cleanups = bootstrapUiCleanups();
  // Snapshot and clear before running: a cleanup that renders again would
  // otherwise register into the set being iterated.
  const pending = [...cleanups];
  cleanups.clear();
  for (const cleanup of pending) cleanup();
}

interface SyncBootstrapUiOptions {
  track?: boolean;
  // Bypasses the matched rule's displayDelay; the rule itself is still evaluated.
  immediate?: boolean;
}

/**
 * Show or hide the bootstrap UI to match the CURRENT page's display rules.
 * Called from init() and from navigate() (SPA route changes) while the full
 * widget has not yet loaded.
 *
 * Acts only on an actual change of verdict: when the verdict is unchanged (e.g. a
 * matching → matching SPA route change) it leaves the current UI untouched, so a
 * shopper mid-typing in the composer keeps their text and focus. A verdict that
 * changes which launcher shows counts, not only one that changes whether any does.
 * When a launcher is due it renders after the matched rule's displayDelay (0 =
 * synchronously, so nothing flashes on a suppressed page), mirroring how the full
 * widget delays its own verdict — the two stay in step on timing as well as on the
 * face. `immediate` skips that wait for a caller restoring a UI whose delay has
 * already elapsed once.
 *
 * `track` fires the one-shot "widget loaded" analytics on the first render of a
 * session (init OR a suppressed → matching navigate); the event's own hasFired
 * guard keeps it to a single emission per session across both entry paths.
 *
 * Internal input↔icon transitions (createMinimizedButton/createChatIconButton
 * calling each other from their handlers) are NOT gated — they only happen once
 * the widget is already shown on a matching page.
 */
function syncBootstrapUi(options: SyncBootstrapUiOptions = {}): void {
  // Callers (init + navigate's bootstrap branch) only invoke this before the
  // full widget loads; the _widgetLoadPromise guard avoids rendering bootstrap
  // UI underneath a full-widget load that's mid-flight (App then owns visibility
  // via its own display-rule evaluation).
  if (!_serverConfig || _widgetLoadPromise) return;

  const { display, displayDelay } = isChatBarVisible(displayRuleInputs());

  // No transition → leave the current UI (and any in-progress composer) alone.
  if (display === _bootstrapUiDisplay) return;
  _bootstrapUiDisplay = display;

  // A prior pending delayed render no longer reflects the current page.
  clearBootstrapDelayTimeout();

  if (display === CHAT_BAR_DISPLAY.HIDDEN) {
    logger.log('[bootstrap] Display rules suppress the chat entry point on this page');
    teardownBootstrapUi();
    // The bar took the shopper's keystroke and is now gone with no widget
    // behind it, so nothing left on this page would claim or drop its socket.
    coolBeaconConnection();
    // Remove the container outright (not just its shadow content) so assistive
    // tech doesn't announce an empty "Live chat" region on a suppressed page.
    // createChatContainer() re-creates it if a later navigate un-suppresses.
    document.getElementById(HOST_CONTAINER_ID)?.remove();
    return;
  }

  if (displayDelay > 0 && !options.immediate) {
    // A launcher already up (the bar or button the page just left wanted) stays until
    // the new one renders, which replaces it. The widget tier does the same, and there
    // it has to: taking the launcher down means suppressing, which takes an open panel
    // with it.
    _bootstrapDelayTimeout = setTimeout(() => {
      _bootstrapDelayTimeout = null;
      renderBootstrapUi({ track: options.track });
    }, displayDelay);
  } else {
    renderBootstrapUi({ track: options.track });
  }
}

/** The page-visibility inputs, read off whatever config is currently loaded. */
function displayRuleInputs() {
  return {
    defaultDisplay: _serverConfig?.v2?.display,
    displayRules: _serverConfig?.v2?.behavior?.displayRules,
    launchers: _serverConfig?.v2?.launchers,
  };
}

/**
 * Render the bootstrap UI: the chat icon if the shopper previously closed it,
 * otherwise the composer bar. Only reached once a page's display rules allow the
 * widget (directly, or after the matched rule's displayDelay).
 */
function renderBootstrapUi(options: { track?: boolean }): void {
  renderBootstrapUiNow(options);

  // The element can arrive later — still parsing, or injected after hydration.
  // Draw ours and switch if one turns up: waiting instead would cost every
  // Legacy UI page time-to-launcher to spare a flash on the rare page with one.
  if (!isLegacyUIEnabled(_serverConfig)) return;
  // Re-armed rather than left in place, so the callback closes over this page's
  // options rather than an earlier page's.
  stopSwitchWatch();
  _cancelSwitchWatch = watchCustomButton(() => {
    // The widget tier owns the verdict from here and watches for itself. Only
    // this watch stands down: the direct-load watch observes the same node, so
    // disconnecting it here would drop a callback already queued behind ours.
    if (_widgetLoadPromise || _isWidgetLoaded) {
      stopSwitchWatch();
      return;
    }
    // Re-read rather than trusting the latch, which syncBootstrapUi advances before
    // the delayed render is scheduled — so a page inside its displayDelay reads as
    // due a launcher with nothing rendered yet.
    if (_bootstrapDelayTimeout || isChatBarVisible(displayRuleInputs()).display === CHAT_BAR_DISPLAY.HIDDEN) return;
    // Same branch again, so this also switches back if the element goes away.
    renderBootstrapUiNow(options);
  });
}

/**
 * Reveal a customer-drawn launcher on the returning-user path, which loads the
 * widget directly and so has no bootstrap render to do it. Without this their
 * button sits invisible for the whole widget-chunk load.
 */
function revealCustomButtonForDirectLoad(): void {
  const reveal = () => {
    if (!isCustomButtonActive(_serverConfig)) return;
    // The kill switch, which otherwise only the widget tier reads.
    if (_dynamicConfig?.enabled === false) return;
    // An outstanding delay is left for the widget tier, which owns the wait. The face the
    // rules asked for is theirs to draw, not ours: a customer button is the only launcher
    // on the page either way.
    const { display, displayDelay } = isChatBarVisible(displayRuleInputs());
    if (display === CHAT_BAR_DISPLAY.HIDDEN || displayDelay > 0) return;
    // A restored Panel re-opens, so revealing the launcher for the chunk load
    // only to hide it as the panel paints is worse than leaving it alone.
    if (getPersistedState()?.ui?.displayMode !== DisplayMode.Minimized) return;
    // Credentials exist on this path by definition, but the user record they
    // belong to may not — same predicate as the widget tier either way.
    applyCustomButtonState({
      hasAuthenticated: getPersistedState()?.user?.id != null,
      hasUnread: false,
      show: true,
    });
  };

  if (isCustomButtonPresent()) {
    reveal();
    return;
  }
  if (!isLegacyUIEnabled(_serverConfig)) return;
  // init() can run more than once; without this the earlier observer is orphaned.
  _cancelDirectLoadWatch?.();
  _cancelDirectLoadWatch = watchCustomButton(() => {
    if (isWidgetMounted()) {
      _cancelDirectLoadWatch?.();
      _cancelDirectLoadWatch = null;
      return;
    }
    reveal();
  });
}

function renderBootstrapUiNow(options: { track?: boolean }): void {
  // The kill switch, which otherwise only the widget tier consults.
  if (isCustomButtonActive(_serverConfig) && _dynamicConfig?.enabled !== false) {
    logger.log('[bootstrap] Custom chat button on the page, deferring the launcher to it');
    // Only `show`: no store here, so unread is unknowable. The widget tier
    // settles the rest once it mounts.
    // Before the write, not after: registering runs the PREVIOUS teardown, which
    // on a second pass through here is also hideCustomButton — and that would
    // strip the class straight back off the element just written to.
    registerBootstrapUiTeardown(hideCustomButton);
    applyCustomButtonState({
      // user.id, matching the widget tier and V1's isExistingUserSelector. The
      // credential pair is persisted separately and can be absent while an id
      // exists, which would put a different face on the button until mount.
      hasAuthenticated: getPersistedState()?.user?.id != null,
      hasUnread: false,
      show: true,
    });
    // Switching away from our own launcher leaves the host empty, and a labelled
    // region with nothing in it announces chat we no longer draw.
    setHostLandmark(false);
    // An entry point was presented, even though we did not draw it.
    if (options.track) _bootstrapAnalytics?.trackWidgetLoaded(WIDGET_STATES.BUBBLE);
    return;
  }

  const appMountElem = createChatContainer();
  // Re-read rather than threaded down from syncBootstrapUi: a delayed render fires long
  // after the verdict was taken, and the custom-button watch calls straight in here. Same
  // reason getBootstrapClosedState() is read here rather than latched.
  const buttonOnly = isChatBarVisible(displayRuleInputs()).display === CHAT_BAR_DISPLAY.BUTTON;
  // The Legacy UI never shows the inline-input launcher; it always uses the
  // plain corner bubble, matching the legacy V1 widget. A page whose rules asked for the
  // button, or a configuration that asks for it everywhere, lands on the same bubble,
  // which is what the widget tier draws there too.
  if (buttonOnly || getBootstrapClosedState() || isButtonLauncherForced(_serverConfig)) {
    logger.log('[bootstrap] Showing the chat icon rather than the bar');
    createChatIconButton(appMountElem);
    if (options.track) _bootstrapAnalytics?.trackWidgetLoaded(WIDGET_STATES.BUBBLE);
  } else {
    createMinimizedButton(appMountElem);
    if (options.track) _bootstrapAnalytics?.trackWidgetLoaded(WIDGET_STATES.DEFAULT);
  }
  // Labelled only once something is in it: the widget tier creates this same host long before its chunk draws.
  setHostLandmark(true);
}

/**
 * Put the page's chat entry point back after a full-widget load failed.
 *
 * Routed through syncBootstrapUi so the restore obeys the page's display rules.
 * The rule's displayDelay is skipped only where the page was already due an
 * entry point, so the wait has at least started. The latch reset is what lets
 * the call act at all:
 * teardownBootstrapUi leaves _bootstrapUiDisplay on its pre-teardown value,
 * which syncBootstrapUi reads as "no transition".
 *
 * Gated on the mount because startFullWidget publishes the action environment
 * long before it mounts React: rebuilding the bootstrap UI wipes the shadow
 * root, which would take a mounted widget down with it.
 */
export function _restoreEntryPoint(): void {
  if (isWidgetMounted()) {
    return;
  }
  // True once the page is due an entry point, whether it rendered or is still
  // inside its delay; the persisted-credentials path loads without ever being
  // due one.
  const wasEntryPointDue = _bootstrapUiDisplay !== null && _bootstrapUiDisplay !== CHAT_BAR_DISPLAY.HIDDEN;
  _bootstrapUiDisplay = null;
  // The persisted-credentials path never built one; this entry point is the page's first.
  // Test sessions publish nothing, and this gateway sits outside configureGateways' test-mode rule.
  if (!_bootstrapAnalytics && !bootedInTestMode() && _initConfig && _serverConfig) {
    _bootstrapAnalytics = newBootstrapAnalytics(_initConfig, _serverConfig);
  }
  // An entry point shown before the load already reported; the one-shot guard stops a repeat.
  syncBootstrapUi({ immediate: wasEntryPointDue, track: true });
}

function newBootstrapAnalytics(initConfig: InitConfiguration, serverConfig: ServerConfiguration): BootstrapAnalytics {
  return createBootstrapAnalytics({
    appId: initConfig.appId,
    clientId: _clientIdManager?.getClientId() ?? '',
    deviceConfig: _deviceConfig,
    gladlyCustomerId: getPersistedState()?.user?.gladlyCustomerId ?? undefined,
    googleAnalyticsGateway: createGoogleAnalyticsGateway(serverConfig),
    serverConfig,
  });
}

export interface InitOptions {
  dynamicConfiguration?: DynamicConfiguration | null;
  serverConfiguration?: ServerConfiguration | null;
}

/**
 * Initialize the Gladly widget
 * Loads configuration, checks for persisted credentials, and shows appropriate UI
 */
export async function init(
  config: InitConfiguration,
  deviceConfig: DeviceConfig = { platform: PLATFORMS.WEB },
  options?: InitOptions,
): Promise<void> {
  try {
    logger.log('[bootstrap] init() called', { config, deviceConfig });
    _initComplete = false;

    // Set appId for storage scoping - needed for the full widget when it loads
    setAppIdForStorage(config.appId || null);

    // Must follow setAppIdForStorage: the persisted slice lives under the
    // appId-scoped key, so resolving first would read the default scope and boot
    // production for an author whose test mode is recorded against their appId.
    _transportDecision = resolveChatTransportDecision();

    // Create the client id manager once so the same id is reused across
    // bootstrap and the full widget. The id is mirrored into the store
    // once the full widget bundle loads.
    if (config.appId) {
      _clientIdManager = new ClientIdManager(config.appId);
    }

    // Store configuration for later use (when loading widget from CDN)
    _initConfig = config;
    _deviceConfig = deviceConfig;
    // Fresh init always (re)evaluates and renders, independent of any prior run.
    _bootstrapUiDisplay = null;

    const preFetchedServer = options?.serverConfiguration;
    const preFetchedDynamic = options?.dynamicConfiguration;
    // Only V1 hands the configuration over, so this is also what says a failure
    // out of init() reaches V1's fallback rather than the customer's own code.
    const isV1Embed = preFetchedServer != null && preFetchedDynamic != null;
    if (isV1Embed) {
      _serverConfig = preFetchedServer;
      _dynamicConfig = preFetchedDynamic;
      // The V1-embed path assigns the config directly, bypassing
      // validServerConfiguration — so the launcher envelope would otherwise
      // reach the orchestrator unsanitized. Mirror the CDN path here: sanitize a
      // present array, and treat an explicit null as absent, so both paths yield
      // identical launcher state that never aliases V1's config.
      const rawLaunchers: unknown = _serverConfig.v2?.launchers;
      if (rawLaunchers !== undefined) {
        _serverConfig = {
          ..._serverConfig,
          v2: { ..._serverConfig.v2, launchers: rawLaunchers === null ? undefined : sanitizeLaunchers(rawLaunchers) },
        };
      }
      logger.log('[bootstrap] Using pre-fetched configuration from V1');
    } else {
      // Load configuration from CDN (similar to V1's configureApp).
      // Fallback :4269 matches V2 standalone dev server; when V1 loads V2 it passes gladlyCdn (no fallback).
      const cdn = config.gladlyCdn || `${window.location.protocol}//localhost:4269`;

      logger.log('[bootstrap] Loading configuration from CDN...', { cdn });
      const configuration = await fetchAppConfiguration(cdn, config);
      _serverConfig = configuration.serverConfiguration;
      _dynamicConfig = configuration.dynamicConfiguration;
    }

    const isLocalCdn = (() => {
      try {
        return new URL(config.gladlyCdn || '').hostname === 'localhost';
      } catch {
        return false;
      }
    })();

    if (config.clusterUrl && _serverConfig && isLocalCdn) {
      _serverConfig = { ..._serverConfig, clusterUrl: config.clusterUrl };
    }

    // Last, so the host page's override beats both the stored configuration and
    // V1's hand-off, and so everything below reads the merged value: the legacy
    // UI gates in this file, the launchers, the bootstrap analytics, and the
    // config this bundle later hands to startFullWidget.
    if (_serverConfig) {
      _serverConfig = applyConfigurationOverride(_serverConfig, config.configurationOverride);
    }

    logger.log('[bootstrap] Configuration loaded', {
      dynamicConfig: _dynamicConfig
        ? {
            availability: _dynamicConfig.availability,
            enabled: _dynamicConfig.enabled,
            percentageRollout: _dynamicConfig.percentageRollout,
          }
        : null,
      serverConfig: _serverConfig
        ? {
            provider: _serverConfig.provider,
            v2Enabled: _serverConfig.v2Enabled,
            widgetType: _serverConfig.widgetType,
          }
        : null,
    });

    if (!_serverConfig) {
      throw new Error('Server configuration is missing');
    }
    if (!_serverConfig.v2Enabled) {
      throw new Error('V2 widget is not enabled in server configuration');
    }

    // Latched here rather than at the end of init(): everything below is the
    // entry point's own rendering, and the paths that skip it (a returning user
    // whose credentials load the widget directly) leave the configuration just
    // as usable. Everything a driver needs is settled at this line.
    _initComplete = true;

    // Before either path forks, so returning (authenticated) users get the same
    // transport as bootstrap users. Arming resolves config and opens nothing.
    startBeaconPipeline();

    // Separate from the pipeline above, which needs a WebMCP manifest and a conversation.
    startPageViewTracking();

    // Before the credentials fork too: capture must not depend on whether the widget loads.
    startOrderCaptureTracking();

    // Before the credentials fork so a returning user's page gets its launchers, and
    // unawaited: a launcher must never delay the widget or fail init().
    void startLaunchers(_serverConfig.v2, {
      openWidgetAndSend: _openWidgetAndSend,
      onWidgetClosed: (listener) => on('sidekick:closed', listener),
      pagePrompts: _pagePrompts,
      featureFlags: _dynamicConfig?.featureFlags,
    });

    // A failed load leaves the previous config's instance alive, flush interval and unload listeners included.
    _bootstrapAnalytics?.destroy();
    _bootstrapAnalytics = null;

    // Check if we have persisted user credentials - if so, load full widget directly
    // This matches the old behavior where shouldLoadFullApp checks hasUserCredentialsSelector
    // A persisted test session stands in for credentials, which test mode never persists.
    const persistedCredentials = getPersistedUserCredentials();
    const resumesTestSession = bootedInTestMode() && currentTestModeState()?.sessionId !== undefined;
    if ((persistedCredentials?.userId && persistedCredentials?.token) || resumesTestSession) {
      logger.log(
        resumesTestSession
          ? '[bootstrap] Test session found in persisted state, loading full widget...'
          : '[bootstrap] User credentials found in persisted state, loading full widget...',
      );
      createChatContainer();
      revealCustomButtonForDirectLoad();
      await loadFullWidget({ handOffToV1OnFailure: isV1Embed });
      return;
    }

    // A widget already on the page owns it, and owns analytics with it. Everything
    // below rebuilds the pre-widget entry point, which renders by wiping the shadow
    // root the widget mounted into — so a second init() would take it down.
    if (isWidgetMounted()) {
      logger.log('[bootstrap] Bootstrap init complete, widget already mounted');
      return;
    }

    _bootstrapAnalytics = newBootstrapAnalytics(config, _serverConfig);

    // Render the bootstrap UI only if this page's display rules allow it. The
    // full widget evaluates the same rules (see evaluateDisplayRules), but the
    // bootstrap UI renders BEFORE the full widget loads, so it must gate itself
    // too — otherwise it shows on every page regardless of the customer's rules.
    // Computed synchronously so nothing flashes on a suppressed page.
    syncBootstrapUi({ track: true });

    logger.log('[bootstrap] Bootstrap init complete');
  } catch (error) {
    logger.error('[bootstrap] Error during init:', error);
    throw error;
  }
}

/**
 * Get current chat availability
 */
// Always returns AVAILABLE regardless of server config because the AI is always available.
export function getAvailability(): string {
  return 'AVAILABLE';
}

/**
 * Get details about this widget and its enabled features.
 */
export function getInfo(): WidgetInfo | undefined {
  if (!_initComplete) {
    return undefined;
  }

  const featureFlags = Object.entries(_dynamicConfig?.featureFlags ?? {})
    .filter(([, enabled]) => enabled === true)
    .map(([name]) => name)
    .sort();

  return { featureFlags, version: WIDGET_VERSION };
}

/**
 * Get the widget's current runtime status without loading or opening it.
 */
export function getStatus(): WidgetStatus {
  const state = _storeApi?.getState();

  return {
    hasActiveConversation: _isWidgetLoaded && state ? getHasActiveConversation(state) : undefined,
    isInitialized: _initComplete,
    isWidgetLoaded: _isWidgetLoaded,
    isWidgetOpen: _isWidgetLoaded && state ? isPanelOnScreen(state) : false,
  };
}

/**
 * Subscribe to events.
 */
export function on(eventName: string, func: (...args: unknown[]) => void): () => void {
  if (!emitter.isValidEvent(eventName)) {
    // Don't throw on an unrecognized event name. A host site that registers a
    // deprecated/renamed/typo'd event (e.g. the v1-only `search:result-selected`,
    // dropped from v2's WIDGET_EVENTS) must not have its own page scripts crash.
    // Warn and return a no-op unsubscribe so the caller degrades gracefully.
    logger.warn('[bootstrap] ignoring unknown eventName: ' + eventName);
    return () => {};
  }

  if (typeof func !== 'function') {
    throw new Error('must pass a valid function as second parameter');
  }

  const id = `obs_${++nextObserverId}`;
  emitter.addObserver(eventName, id, func);

  return () => {
    emitter.removeObserver(eventName, id);
  };
}

/**
 * Show the widget
 */
export async function show(): Promise<void> {
  const reported = trackEntryPointOpen('API');
  warmBeaconConnection();
  if (!_isWidgetLoaded) {
    await loadFullWidget({});
  }

  revealAfterDeliberateOpen();

  // _actionEnv is always set after loadFullWidget completes (startFullWidget calls _setActionEnvironment)
  if (_actionEnv) {
    const { unminimizeWidget } = await import('@/actions/ui/unminimizeWidget');
    unminimizeWidget(_actionEnv, 'API', { trackViewChange: !reported });
  }
}

// Opening the widget is the shopper acting on the entry point the page allowed, so
// the page's verdict — including a delay still running — must not hold the widget
// they opened back. The bootstrap paths call this after their load, which is the
// first moment a store exists.
function revealAfterDeliberateOpen(): void {
  // Ungated: the socket is bootstrap-tier, and startFullWidget publishes the
  // environment only when window.Gladly survived — a third-party script that
  // clobbered it leaves a mounted widget with none, and the claim still belongs
  // to the panel on screen.
  beaconWidgetOpened();
  _actionEnv?.store.uiMutations.showWidgetRequestedByApi();
}

/**
 * Close the widget
 */
export async function close(): Promise<void> {
  if (!_isWidgetLoaded) return;

  // _actionEnv is always set after loadFullWidget completes (startFullWidget calls _setActionEnvironment)
  if (_actionEnv) {
    const { minimizeWidget } = await import('@/actions/ui/minimizeWidget');
    minimizeWidget(_actionEnv);
  }
}

/**
 * Set user
 */
export async function setUser(user: {
  name?: string;
  email?: string;
  phoneNumber?: string;
  jwt?: string | (() => Promise<string>);
}): Promise<void> {
  if (!_isWidgetLoaded) {
    // No trackEntryPointOpen: identifying a shopper reveals nothing, so the load
    // it forces is not an open.
    await loadFullWidget({});
  }

  const state = getStore().getState();

  if (hasUser(state.user)) {
    throw new Error('error setting user - user already set');
  }

  let { jwt, name } = user;
  const { email, phoneNumber } = user;

  if (jwt) {
    if (email || phoneNumber) {
      throw new Error('error setting user - cannot set email or phoneNumber when jwt is provided');
    }

    switch (typeof jwt) {
      case 'string': {
        const token = jwt;
        jwt = async () => token;
        break;
      }
      case 'function':
        break;
      default:
        throw new Error('error setting user - jwt must be of type string or function');
    }
    name = name || '';
    state.userMutations.userSetByGladlyApi({ name, jwt });
    return;
  }

  name = name?.trim() ?? '';
  if (!name) {
    throw new Error('error setting user - name is blank');
  }

  if (typeof email === 'string') {
    if (phoneNumber) {
      throw new Error('error setting user - cannot set phoneNumber when email is provided');
    }
    if (!isValidEmail(email)) {
      throw new Error('error setting user - invalid email');
    }
    state.userMutations.userSetByGladlyApi({ name, email });
    return;
  }

  if (typeof phoneNumber === 'string') {
    if (!/^\+?[0-9]+$/.test(phoneNumber)) {
      throw new Error('error setting user - invalid phone number');
    }
    state.userMutations.userSetByGladlyApi({ name, phoneNumber });
    return;
  }

  throw new Error('error setting user - must provide either email or phoneNumber');
}

/**
 * Get user. Returns PublicUser or undefined if no user is set.
 */
export function getUser(): PublicUser | undefined {
  if (!_storeApi) {
    return undefined;
  }
  const state = _storeApi.getState();

  if (!hasUser(state.user)) {
    return undefined;
  }

  const hasOpenConversation = getHasActiveConversation(state);

  return {
    name: state.user.name,
    type: state.user.type ?? UserType.ANONYMOUS,
    identityType: state.user.identityType ?? UserIdentityType.EMAIL,
    identity: state.user.identity ?? '',
    hasOpenConversation,
  };
}

/**
 * Clear user.
 *
 * Clears the local user and conversation whenever a store has been handed over,
 * loading the widget bundle first if that is what it takes to reach the chat
 * gateway's logout. `resetConversation` owns the logout and the teardown
 * ordering; this owns the load and the no-environment fallback.
 */
export async function clearUser(): Promise<void> {
  _pendingToolStatus = null;
  if (!_storeApi) {
    return;
  }
  const store = _storeApi.getState();

  try {
    // No trackEntryPointOpen: the load is here to reach the chat gateway's
    // logout, and nothing is revealed by it. No `_isWidgetLoaded` check —
    // loadFullWidgetOnce owns that one, and on an already-loaded widget its
    // early return is unobservable from here.
    //
    // A pre-load snapshot, so it can disagree with resetConversation's live
    // read: rehydration during the load can null the credentials, leaving a
    // load whose logout is then a no-op. Harmless — the gateway guards on its
    // own client, and the teardown runs from the `finally` either way.
    if (hasUserCredentials(store.user)) {
      await loadFullWidget({});
    }
  } catch (error) {
    logger.error('[bootstrap] clearUser: error loading the widget', error);
  }

  if (_actionEnv) {
    await resetConversation(_actionEnv);
    return;
  }
  // No action environment means no gateway to log out of — the load above failed,
  // or startFullWidget threw between handing over the store and the environment.
  // Clear this side anyway: this is the API for forgetting a user.
  resetConversationLocally(store);
}

/**
 * Options for `startConversation`. Mirrors V1's `Gladly.StartConversationOptions`.
 */
export interface StartConversationOptions {
  /**
   * Context the integrator already has about the customer's prior interactions — an earlier
   * conversation with the brand's own website assistant, a recent marketing email, an order
   * placed last week. Never shown to the customer and never part of the transcript: it rides
   * the initial message's `initialData`.
   * `null` means the same as leaving it out — the mobile SDKs forward an explicit null.
   */
  priorConversationSummary?: string | null;
  /** Leave out what Chat shows before the customer has said anything. */
  suppressWelcomeMessage?: boolean;
}

const START_CONVERSATION_OPTIONS = ['priorConversationSummary', 'suppressWelcomeMessage'];
const PRIOR_CONVERSATION_SUMMARY_MAX_LENGTH = 5000;

/**
 * Start a conversation
 */
export async function startConversation(
  messages: Array<{ type: string; text: string }>,
  options: StartConversationOptions = {},
): Promise<void> {
  if (!Array.isArray(messages)) {
    throw new Error('messages must be an array');
  }
  if (!messages.length) {
    throw new Error('messages must contain at least one message');
  }

  if (
    messages.some(
      (message) =>
        typeof message !== 'object' ||
        message === null ||
        typeof message.type !== 'string' ||
        message.type !== 'TEXT' ||
        typeof message.text !== 'string' ||
        message.text.trim() === '',
    )
  ) {
    throw new Error("Each item in messages must be an object with type 'TEXT' and a non-empty string 'text'");
  }

  if (typeof options !== 'object' || options === null || Array.isArray(options)) {
    throw new Error('options must be an object');
  }

  // Rejected rather than ignored: a misspelling would otherwise be silently dropped, and the
  // integrator would have no signal that the context they handed over never arrived.
  const unknownOptions = Object.keys(options).filter((key) => !START_CONVERSATION_OPTIONS.includes(key));
  if (unknownOptions.length) {
    throw new Error(`unknown options: ${unknownOptions.join(', ')}`);
  }

  const { priorConversationSummary, suppressWelcomeMessage = false } = options;
  if (typeof suppressWelcomeMessage !== 'boolean') {
    throw new Error('options.suppressWelcomeMessage must be a boolean');
  }

  // null counts as absent, not as a wrong type: the Android SDK forwards its whole options object
  // and serializes an unset summary as an explicit null rather than leaving the key out.
  if (priorConversationSummary !== undefined && priorConversationSummary !== null) {
    if (typeof priorConversationSummary !== 'string') {
      throw new Error('options.priorConversationSummary must be a string');
    }
    if (priorConversationSummary.length > PRIOR_CONVERSATION_SUMMARY_MAX_LENGTH) {
      throw new Error(
        `options.priorConversationSummary must be ${PRIOR_CONVERSATION_SUMMARY_MAX_LENGTH} characters or fewer`,
      );
    }
  }

  // Safe ahead of the guard: a reporting bootstrap tier means no persisted credentials, so no conversation.
  const reported = trackEntryPointOpen('API');
  warmBeaconConnection();

  if (!_isWidgetLoaded) {
    await loadFullWidget({});
  }

  const state = getStore().getState();
  if (getHasActiveConversation(state)) {
    throw new Error('cannot start conversation with an active conversation');
  }

  // The only rendered echo of an unsent message; without it the panel reads as empty until auth completes.
  _actionEnv?.store.messagesMutations.setPendingInitialMessage(messages[0].text);
  revealAfterDeliberateOpen();

  if (_actionEnv) {
    const actionEnv = _actionEnv;
    if (suppressWelcomeMessage) {
      // Before the messages, so the first of them takes the slot the greeting gives up.
      actionEnv.store.messagesMutations.setWelcomeMessage(null);
    }
    const [{ authOrCreateMessage }, { unminimizeWidget }] = await Promise.all([
      import('@/actions/messages/authOrCreateMessage'),
      import('@/actions/ui/unminimizeWidget'),
    ]);
    // Before the sends: full auth sets Panel without reporting, leaving this open no transition to report.
    if (actionEnv.store.ui.displayMode === DisplayMode.Minimized) {
      unminimizeWidget(actionEnv, 'API', { trackViewChange: !reported });
    }
    // Only the first call carries the summary: sendMessage puts it on initialData, which only the
    // initial message has, so handing it to the rest would just be dropped.
    await Promise.all(
      messages.map((message, index) =>
        index === 0 && priorConversationSummary
          ? authOrCreateMessage(actionEnv, message.text, undefined, { priorConversationSummary })
          : authOrCreateMessage(actionEnv, message.text),
      ),
    );
  }
}

/**
 * Navigate (for SPA support). Re-evaluates display rules and notifies the widget.
 */
export async function navigate(): Promise<void> {
  // Ahead of the launcher re-gate: an AI launcher waits on the request this sends.
  // Guarded: a throw would reject the host's navigate() and skip both re-gates below.
  try {
    _pageViews?.sendIfChanged();
  } catch (error) {
    logger.error('[bootstrap] Page view failed on navigation', error);
  }

  // Above the early return below: launchers exist on pages where the widget never
  // loads, and that return would skip their re-gate on exactly those pages.
  syncLaunchersToUrl();

  // Bootstrap phase (full widget not yet loaded): re-sync the bootstrap UI to
  // the new page's display rules so an SPA route change hides/shows it. Track
  // here too: if the widget was suppressed at init, the first navigate onto a
  // matching page is where it loads, so that's where the "widget loaded" event
  // belongs. The event's hasFired guard prevents a double-count when the session
  // started on a matching page, and syncBootstrapUi no-ops when visibility is
  // unchanged, so matching → matching route changes don't re-fire it.
  //
  // Keyed on the mount as well as the flag, which latches only once the load
  // settles: a navigation landing in between belongs to the widget, and
  // rebuilding the bootstrap UI would tear its root down. _actionEnv and the
  // store are both published before mountWidget, so the widget path below has
  // what it needs.
  if (!_isWidgetLoaded && !isWidgetMounted()) {
    syncBootstrapUi({ track: true });
    return;
  }

  if (_actionEnv) {
    const { evaluateDisplayRules } = await import('@/actions/configuration/evaluateDisplayRules');
    await evaluateDisplayRules(_actionEnv);
  }

  _storeApi?.getState().appMutations.pageLoaded();
}

/**
 * Show greeting
 */
// No-op: proactive greetings are not implemented in V2. Stub kept for public API compatibility.
export function showGreeting(message: string): void {
  logger.log('[bootstrap] showGreeting() called', message);
}

/**
 * Skip onboarding
 */
// No-op: onboarding is not implemented in V2. Stub kept for public API compatibility.
export function skipOnboarding(): void {
  logger.log('[bootstrap] skipOnboarding() called');
}

/**
 * Apply campaign
 */
// No-op: campaigns are not implemented in V2. Stub kept for public API compatibility.
export function applyCampaign(campaignId: string): void {
  logger.log('[bootstrap] applyCampaign() called', campaignId);
}

/**
 * Reconnect (private API for iOS SDK)
 */
export async function reconnect(): Promise<void> {
  if (!_actionEnv) return;
  await _actionEnv.gateways.chatGateway.reconnect?.();
}

/**
 * Enable logging in production (useful for debugging)
 * Can be called from console: Gladly._showLogging()
 */
export function _showLogging(): void {
  enableLogging();
}

/**
 * Internal API: Set the store instance from startFullWidget
 * This allows the bootstrap bundle to avoid bundling the store implementation
 */
export function _setStore(store: WidgetStoreApi): void {
  _storeApi = store;
  logger.log('[bootstrap] Store instance set');
}

/**
 * Internal API: Return the bootstrap-owned client id so the full widget can
 * mirror it into the store once available.
 */
export function _getClientId(): string | null {
  return _clientIdManager?.getClientId() ?? null;
}

/**
 * Internal API: Set the action environment from startFullWidget
 * Enables API methods (show, close, etc.) to call proper actions with analytics
 */
export function _setActionEnvironment(env: ActionEnvironment): void {
  _actionEnv = env;
  logger.log('[bootstrap] Action environment set');
}

/** Internal API: shows the status buffered before login, unless it is stale. */
export function _replayPendingToolStatus(): void {
  const pending = _pendingToolStatus;
  _pendingToolStatus = null;
  if (_actionEnv && pending && Date.now() - pending.receivedAt <= PENDING_TOOL_STATUS_MAX_AGE_MS) {
    // Passed whole to keep the always-loaded tier small; the action reads only callId and status.
    _actionEnv.runAction(beaconToolStatusReceived, pending);
  }
}

/**
 * Internal API: Get the event emitter singleton.
 * Used by startFullWidget to pass the emitter to createActionEnvironment.
 */
export function _getEmitter(): ApiEventEmitter {
  return emitter;
}

// Internal API: Test Mode.
//
// The driver-agnostic surface described in `domain/testMode`: an extension, a
// console session or a script drives test mode through these six methods, and
// the widget names no driver of its own.
//
// The two queries answer from configuration and storage alone. A driver calls
// them on every page it runs on, nearly all of which will never test anything,
// so they must stay cheap and free of side effects. The three that change the
// slice load the widget bundle first: the store's persist middleware is its only
// writer, and a driver calls them from pages where that bundle has never loaded.
// `_connectTestChannel` parks the port until a load takes it.

/** The skew handshake, plus the org, cluster and widget a driver needs before entering. */
export function _getTestModeSupport(): TestModeSupport | null {
  // A driver polls until this answers, so `null` has to mean "not yet" rather
  // than "not supported" — which is what it means until init() has a usable
  // configuration. Keyed on the latch rather than on `_serverConfig` alone,
  // which is assigned before the `v2Enabled` gate that can abort init.
  if (!_initComplete || !_initConfig || !_serverConfig) {
    return null;
  }
  return {
    apiVersion: TESTER_PROTOCOL_VERSION,
    appId: _initConfig.appId,
    gladlySite: _serverConfig.gladlySite,
    orgId: _serverConfig.orgId,
  };
}

/** What this page load is testing, and whether it still wants a channel. */
export function _getTestModeStatus(): TestModeStatus | null {
  // init() is what scopes storage to the appId and resolves the decision below,
  // so before it there is no answer to give rather than a negative one.
  if (!_initComplete) {
    return null;
  }
  return toTestModeStatus(bootedInTestMode(), currentTestModeState(), _testChannelPort !== null);
}

/**
 * Accept the driver's `MessagePort` for this page load. First delivery wins.
 * Throws when `port` is not a MessagePort or the page is not awaiting one, so
 * the driver closes its end.
 */
export function _connectTestChannel(port: unknown): void {
  assertInitialized();
  if (!(port instanceof MessagePort)) {
    throw new Error('Gladly: _connectTestChannel needs a MessagePort');
  }
  if (!_getTestModeStatus()?.awaitingChannel) {
    throw new Error('Gladly: this page load is not awaiting a test channel');
  }
  _testChannelPort = port;
  _testChannelConsumer?.(port);
}

/**
 * The mutators refuse before init() rather than reporting a result, and all
 * refuse the same way. `_transportDecision` is `production` from its
 * declaration, so gating on it alone let a pre-init exit or restart resolve
 * silently — telling a driver the mode was left while the persisted slice, and
 * so the next page load, said otherwise.
 */
function assertInitialized(): void {
  if (!_initComplete) {
    throw new Error('Gladly: test mode needs init() to have run');
  }
}

/**
 * Whether this page load runs the tester transport: the boot decision, and the
 * slice still active. Before the widget bundle loads the slice is the persisted
 * one the decision was read from; after, it is the store's, which every fallback
 * to production in `configureGateways` retires.
 */
function bootedInTestMode(): boolean {
  return _transportDecision === 'gladosTester' && currentTestModeState()?.active === true;
}

export async function _enterTestMode(config: unknown): Promise<void> {
  assertInitialized();
  // Throws on a config a driver got wrong instead of quietly booting production.
  // Ahead of the load below, so bad input costs nothing.
  const validated = validTestModeConfig(config);
  // A driver may re-signal the mode it is already in — a toggle clicked twice,
  // or a driver that signals per page load instead of trusting the persisted
  // slice — and reloading on each one would leave the page in a loop.
  if (bootedInTestMode() && isTestingWith(currentTestModeState(), validated)) {
    return;
  }
  await enterTestMode(await testModeEnvironment(), validated);
}

/** Leave test mode and boot as the production widget. No-op off a test-mode boot. */
export async function _exitTestMode(): Promise<void> {
  assertInitialized();
  if (!bootedInTestMode()) {
    return;
  }
  await exitTestMode(await testModeEnvironment());
}

/** Drop the test session, keep the profile under test. No-op off a test-mode boot. */
export async function _restartTestConversation(): Promise<void> {
  assertInitialized();
  if (!bootedInTestMode()) {
    return;
  }
  await restartTestConversation(await testModeEnvironment());
}

/**
 * The live test-mode state: the store owns it once the widget bundle is up.
 *
 * Before that the persisted blob stands in, and it goes through the same
 * validator the store's rehydration uses. `mergePersistedState` has not run on
 * this path, and on a test-mode boot with no session to resume this is the
 * *normal* path rather than an edge case — no credentials persist, so nothing
 * auto-loads the bundle. Without
 * the validation the API would report host-page-writable values through fields
 * it declares as `string`.
 */
function currentTestModeState(): TestModeState | null | undefined {
  return _storeApi?.getState().testMode ?? toRehydratedTestModeState(getPersistedState()?.testMode);
}

/**
 * The action environment, loading the widget bundle if this page has not needed
 * it yet — the usual case on a boot with no session to resume, since test mode
 * persists no credentials and only a persisted session takes init()'s auto-load
 * path.
 */
async function testModeEnvironment(): Promise<ActionEnvironment> {
  if (!_isWidgetLoaded) {
    try {
      await loadFullWidget({});
    } catch (error) {
      // loadFullWidget rethrows whatever failed inside it — a chunk that would
      // not fetch, a configuration that was never initialized. A driver is owed
      // the difference between "done" and "could not", not the widget's
      // internals, so the cause is carried rather than forwarded.
      throw new Error('Gladly: test mode needs the widget, which failed to load', { cause: error });
    }
  }
  if (!_actionEnv) {
    // A load that resolved without publishing an environment: startFullWidget
    // publishes under an `if (gladlyApi)` and resolves normally when that guard
    // fails. Everything else about that state is broken too — setUser, the
    // emitter, the store handoff — so refusing is right, but it is a shape that
    // happens rather than one that cannot.
    throw new Error('Gladly: test mode needs the widget, which loaded without an environment');
  }
  return _actionEnv;
}

export const _openWidgetAndSend: OpenWidgetAndSend = createOpenWidgetAndSend({
  getActionEnv: () => _actionEnv,
  isWidgetLoaded: () => _isWidgetLoaded,
  // Called through rather than passed by value: this runs at module init, so a
  // by-value reference would throw once loadFullWidget stops being hoisted.
  loadFullWidget: (options) => loadFullWidget(options),
  trackWidgetOpened: () => trackEntryPointOpen('USER_INTERACTION'),
});

function getStore(): WidgetStoreApi {
  if (!_storeApi) {
    throw new Error('Gladly widget not initialized. Call init() first.');
  }
  return _storeApi;
}

/**
 * Create the DOM element for the widget container.
 * Idempotent: returns existing element if present, never creates duplicates.
 * Prepends to body so screen readers encounter the widget early in tab order.
 */
export function createChatContainer(): HTMLDivElement {
  const zIndex = _serverConfig?.v2?.appearance?.zIndex ?? DEFAULT_Z_INDEX;
  const existingElem = document.getElementById(HOST_CONTAINER_ID);
  if (existingElem && existingElem instanceof HTMLDivElement) {
    // Ensure it's the first child of body for screen reader tab order. Only when
    // it isn't already: prepending re-inserts, which tears down and rebuilds the
    // navigable of any iframe inside — and the Beacon sandbox lives in this
    // host's shadow root, with the parent holding its contentWindow.
    if (document.body.firstChild !== existingElem) {
      document.body.prepend(existingElem);
    }
    // The V1 bundle may have created this element and pinned its z-index via
    // global.css; own the host's stacking so the configured value wins.
    applyHostZIndex(existingElem, zIndex);
    return existingElem;
  }

  const appMountElem = document.createElement('div');
  appMountElem.id = HOST_CONTAINER_ID;
  appMountElem.className = 'preload';
  applyHostZIndex(appMountElem, zIndex);
  document.body.prepend(appMountElem);

  return appMountElem;
}

/**
 * Create a paper airplane icon SVG (for submit button)
 */
function createPaperAirplaneIcon(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svg.style.cssText = 'flex-shrink: 0; cursor: pointer;';

  // Create clip path group
  const g = document.createElementNS('http://www.w3.org/2000/svg', 'g');
  g.setAttribute('clip-path', 'url(#clip0_6371_191)');

  // Create path
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute(
    'd',
    'M0.1063 2.44693C-0.0812003 2.07193 -0.0155753 1.61881 0.271925 1.31256C0.559425 1.00631 1.0063 0.915685 1.3938 1.07818L15.3938 7.07818C15.7625 7.23443 16 7.59693 16 7.99693C16 8.39693 15.7625 8.75943 15.3938 8.91568L1.3938 14.9157C1.00942 15.0813 0.559425 14.9876 0.271925 14.6813C-0.0155753 14.3751 -0.0812003 13.9219 0.1063 13.5469L2.8813 8.00006L0.1063 2.44693ZM3.75005 8.50006L1.00005 14.0001L13.8344 8.50006H3.75005ZM13.8344 7.50006L1.00005 2.00006L3.75005 7.50006H13.8344Z',
  );
  path.setAttribute('fill', 'currentColor');
  g.appendChild(path);

  // Create defs with clipPath
  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
  const clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
  clipPath.setAttribute('id', 'clip0_6371_191');
  const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
  rect.setAttribute('width', '16');
  rect.setAttribute('height', '16');
  rect.setAttribute('fill', 'white');
  clipPath.appendChild(rect);
  defs.appendChild(clipPath);

  svg.appendChild(g);
  svg.appendChild(defs);

  return svg;
}

/**
 * Create an X close icon SVG
 */
function createXCloseIcon(): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', '16');
  svg.setAttribute('height', '16');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  svg.style.cssText = 'display: block; flex-shrink: 0;';

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute(
    'd',
    'M13.1465 2.14648C13.3417 1.95122 13.6583 1.95122 13.8535 2.14648C14.0488 2.34175 14.0488 2.65825 13.8535 2.85352L8.70703 8L13.8535 13.1465C14.0488 13.3417 14.0488 13.6583 13.8535 13.8535C13.6583 14.0488 13.3417 14.0488 13.1465 13.8535L8 8.70703L2.85352 13.8535C2.65825 14.0488 2.34175 14.0488 2.14648 13.8535C1.95122 13.6583 1.95122 13.3417 2.14648 13.1465L7.29297 8L2.14648 2.85352C1.95122 2.65825 1.95122 2.34175 2.14648 2.14648C2.34175 1.95122 2.65825 1.95122 2.85352 2.14648L8 7.29297L13.1465 2.14648Z',
  );
  path.setAttribute('fill', 'currentColor');
  svg.appendChild(path);

  return svg;
}

function startPageViewTracking(): void {
  stopPageViewTracking();

  if (!_serverConfig || !_initConfig) return;

  // Before the import, not inside it: the module is a dynamic chunk, and an org with the
  // feature off should not fetch it on every page.
  // Identity, not truthiness: the V1-embed path assigns its dynamic config unvalidated,
  // so a string "false" would otherwise turn this on for an org that has it off.
  if (_dynamicConfig?.featureFlags?.pageViews !== true) {
    logger.log('[bootstrap] Page views skipped: pageViews feature flag is off');
    return;
  }

  const options = {
    appId: _initConfig.appId,
    clientId: _clientIdManager?.getClientId() ?? '',
    featureFlags: _dynamicConfig.featureFlags,
    pagePrompts: _pagePrompts,
    serverConfig: _serverConfig,
  };
  const run = ++_pageViewRun;
  // Armed before the import: a launcher whose chunk wins the race must still know an answer is coming.
  _pagePrompts.arm();
  void loadPageViews()
    .then(({ startPageViews }) => {
      // A teardown or a later init happened while the chunk loaded; that one owns the page.
      if (run !== _pageViewRun) return;
      _pageViews = startPageViews(options);
    })
    .catch((error: unknown) => {
      logger.error('[bootstrap] Page views failed to start', error);
      // Unless a later init has since re-armed it for its own chunk.
      if (run === _pageViewRun) _pagePrompts.disarm();
    });
}

function stopPageViewTracking(): void {
  // Bumped so a chunk still loading does not arm against the config it was started with.
  _pageViewRun += 1;
  _pageViews = null;
  _pagePrompts.disarm();
}

/**
 * Capture orders on this page for a shopper who chatted in the window, whether
 * or not the widget opens. The chunk and the manifest request it makes are
 * paid only by those page loads. A shopper outside the window who chats on
 * this page may buy on it too, so the first message sent starts capture then.
 *
 * Page-scoped, like page views: nothing stops it when the widget mounts. One per
 * document, kept across a re-init with the same configuration (see
 * DocumentOrderCapture).
 */
function startOrderCaptureTracking(): void {
  _stopOrderCaptureWait?.();
  _stopOrderCaptureWait = null;

  if (!_serverConfig || !_initConfig || !_clientIdManager) {
    stopOrderCaptureTracking();
    return;
  }

  // Its own gateway, so configureGateways' test-mode rule does not reach it. Test
  // sessions are throwaway, and nothing else the widget sends publishes from them.
  if (bootedInTestMode()) {
    logger.log('[bootstrap] Order capture skipped: test mode');
    stopOrderCaptureTracking();
    return;
  }

  if (!isOrderCaptureOnLoadEnabled(_dynamicConfig?.featureFlags)) {
    logger.log('[bootstrap] Order capture skipped: orderCaptureOnLoad feature flag is off');
    stopOrderCaptureTracking();
    return;
  }

  const manifestUrl = _serverConfig.v2?.beacon?.manifestUrl;
  if (!manifestUrl) {
    logger.log('[bootstrap] Order capture skipped: org has no WebMCP manifest configured');
    stopOrderCaptureTracking();
    return;
  }

  const appId = _initConfig.appId;
  const key = JSON.stringify([appId, manifestUrl]);
  const current = window.__gladlyOrderCapture;
  if (current?.key === key) {
    logger.log('[bootstrap] Order capture kept: this configuration already started it');
    return;
  }
  stopOrderCaptureTracking();

  const options = {
    appId,
    clientId: _clientIdManager.getClientId(),
    getStore: () => _storeApi,
    manifestUrl,
    platform: _deviceConfig.platform,
    serverConfig: _serverConfig,
  };
  const begin = (armedByMessage: boolean): void => {
    _stopOrderCaptureWait?.();
    _stopOrderCaptureWait = null;
    // Another module instance may have started this capture while this one waited.
    if (window.__gladlyOrderCapture?.key === key) return;
    stopOrderCaptureTracking();

    const held: DocumentOrderCapture = { capture: null, key, stopped: false };
    window.__gladlyOrderCapture = held;
    void loadOrderCapture()
      .then(({ startOrderCaptureOnLoad }) => startOrderCaptureOnLoad({ ...options, armedByMessage }))
      .then((capture) => {
        // A teardown or a different configuration replaced it while the chunk loaded.
        if (held.stopped) {
          capture?.dispose();
        } else {
          held.capture = capture;
        }
      })
      .catch((error: unknown) => {
        logger.error('[bootstrap] Order capture failed to start', error);
        // So a later init can try again.
        if (window.__gladlyOrderCapture === held) delete window.__gladlyOrderCapture;
      });
  };

  // Chatted before this page load. Whether the chat came before or after the
  // order is unknowable from the marker -- a shopper who buys, asks "where is
  // my order?", then reloads the confirmation page lands here with the order
  // genuinely first. Flagging on a marker would brand all of those, so this
  // path publishes unflagged and under-reports instead.
  if (isWithinCaptureWindow(readLastEngagedAt(appId), Date.now())) {
    begin(false);
    return;
  }
  logger.log('[bootstrap] Order capture waiting: no chat in the capture window');
  // A send on this page, or a history load that reaches back inside the window.
  _stopOrderCaptureWait = onEngagementRecorded((engagedAppId, sentAt) => {
    // A history fetch from an earlier init can finish after an init for another app.
    if (engagedAppId !== appId || !isWithinCaptureWindow(sentAt, Date.now())) return;
    // Only a send happening now means the engagement is beginning now, which is
    // what makes an order already on the page predate it. A replayed message
    // carries its original date, so a history load reaching back into the
    // window is a chat that may well have come before this order was placed --
    // flagging on that would brand a genuine capture.
    begin(isLiveEngagement(sentAt, Date.now()));
  });
}

function stopOrderCaptureTracking(): void {
  _stopOrderCaptureWait?.();
  _stopOrderCaptureWait = null;
  const held = window.__gladlyOrderCapture;
  if (!held) return;
  // Marked so a chunk still loading disposes what it starts.
  held.stopped = true;
  held.capture?.dispose();
  delete window.__gladlyOrderCapture;
}

function startBeaconPipeline(): void {
  stopBeaconPipeline();

  if (!_serverConfig || !_initConfig) return;

  if (!_dynamicConfig?.featureFlags?.enableBeacon) {
    logger.log('[bootstrap] Beacon pipeline skipped: enableBeacon feature flag is off');
    return;
  }

  if (!_clientIdManager) {
    logger.warn('[bootstrap] Beacon pipeline skipped: clientId manager not initialized');
    return;
  }

  if (!_serverConfig.v2?.beacon?.manifestUrl) {
    logger.log('[bootstrap] Beacon pipeline skipped: org has no WebMCP manifest configured');
    return;
  }

  logger.log('[bootstrap] Beacon pipeline armed');

  // Armed, not connected: no socket exists without a live conversation. Anything
  // that has to reach beacon from a page with no conversation — pageview
  // telemetry, pre-session prompts — needs its own transport rather than an
  // ensureBeaconGateway() call here, which would put the volume back on pageviews.

  const appId = _initConfig.appId;
  const clientId = _clientIdManager.getClientId();
  const serverConfig = _serverConfig;

  configureBeaconTabLock({
    appId,
    create: (hooks) => loadBeaconChunk().then(({ BeaconTabLock }) => new BeaconTabLock(appId, hooks)),
  });
  registerBeaconGatewayFactory((canConnect) => {
    const generation = ++_beaconGatewayGeneration;
    return loadBeaconChunk().then(({ createBeaconGateway }) =>
      createBeaconGateway({
        appId,
        canConnect,
        clientId,
        onToolSettled: (callId) => {
          if (generation === _beaconGatewayGeneration) settleToolStatus(callId);
        },
        onToolStatus: showToolStatus,
        serverConfig,
      }),
    );
  });

  // The teardown above closed any socket this page had, and cleared both
  // claims with it. Neither an open panel nor a conversation already under way
  // produces a further gesture of its own, so without this re-read the page
  // runs unserved — or holds nothing for a shopper sitting one message away.
  if (!_storeApi) {
    return;
  }

  const state = _storeApi.getState();
  rearmBeaconConnection(getHasActiveConversation(state), isPanelOnScreen(state));
}

function loadBeaconChunk(): Promise<typeof import('@/lib/beacon/createBeaconGateway')> {
  return import('@/lib/beacon/createBeaconGateway');
}

/**
 * Whether a panel is on screen right now, matching what App renders on: a
 * mounted widget the rules allow, in a display mode that is not minimized.
 */
function isPanelOnScreen(state: ReturnType<WidgetStoreApi['getState']>): boolean {
  if (!state.app.initialized || state.ui.displayMode === DisplayMode.Minimized) {
    return false;
  }

  return getIsAllowedByDisplayRules({ hasActiveConversation: getHasActiveConversation(state), ui: state.ui });
}

/**
 * Tear down the gateway and disarm the connection. Deliberately partial:
 * deregisters from the heartbeat registry without ending the session, since the
 * chat client is not rebuilt alongside it. Callers that re-arm afterwards are
 * responsible for reconnecting a conversation that is still live.
 */
export function stopBeaconPipeline(): void {
  registerBeaconGatewayFactory(null);
  destroyBeaconGateway();
}

/**
 * Forward a caller's initialMessage to an already-loaded widget. No-op when the
 * caller had no message or the action environment isn't ready yet.
 */
async function forwardInitialMessage(initialMessage?: string): Promise<void> {
  if (!initialMessage) {
    return;
  }
  if (!_actionEnv) {
    logger.warn('[bootstrap] Dropping initialMessage: action environment not ready');
    return;
  }
  const { authOrCreateMessage } = await import('@/actions/messages/authOrCreateMessage');
  await authOrCreateMessage(_actionEnv, initialMessage);
}

interface LoadFullWidgetOptions {
  initialMessage?: string;
  // Set only for the load init() runs itself under V1: V1's fallback answers
  // that failure by mounting its own widget.
  handOffToV1OnFailure?: boolean;
}

/**
 * Report an open driven from outside the bootstrap UI — a launcher prompt, a
 * host-page `show()` — and answer whether the open is now on record, which is
 * what tells the reveal on the far side of the load to stay quiet.
 *
 * Only the bootstrap tier knows the entry point the shopper left, and it stops
 * existing part-way through the load that follows: the widget mounts into
 * whatever display mode was persisted, which says nothing about the transition
 * just made. So callers must reach this before loadFullWidget and pass the
 * answer on. Two tiers each guessing separately count one open twice, or miss
 * it altogether.
 *
 * The chat icon and Chat Bar know their own `fromState` and call
 * reportEntryPointOpen directly.
 */
function trackEntryPointOpen(source: WidgetTransitionSource): boolean {
  // A widget that is up reports its own transitions, off its own display mode.
  if (_isWidgetLoaded) {
    return false;
  }
  // Only the entry point that starts a load reports it; everything arriving
  // behind that load lands in the same open.
  if (_entryPointOpenReported) {
    return true;
  }
  // Mirrors toWidgetState's reading of a minimized widget, blind spot included:
  // a Legacy-UI org renders the bubble whatever the closed state says, and both
  // derivations call that 'default'. Matching the widget tier is the point, so
  // correcting it here alone would only move the inconsistency — the icon's
  // hardcoded 'bubble' below is the one that already disagrees with both.
  const fromState = getBootstrapClosedState() ? WIDGET_STATES.BUBBLE : WIDGET_STATES.DEFAULT;
  return reportEntryPointOpen(fromState, source);
}

/**
 * Publish an open and latch it for the load that follows, so the entry points
 * still clickable during that load — a launcher pill lives outside the widget's
 * shadow host and survives the teardown — do not report it a second time.
 */
function reportEntryPointOpen(fromState: WidgetState, source: WidgetTransitionSource): boolean {
  // A load nobody asked for — persisted credentials — runs before this tier
  // exists. With nothing to report through, the open is the widget's to report.
  if (!_bootstrapAnalytics) {
    return false;
  }
  _bootstrapAnalytics.trackWidgetViewChanged(fromState, WIDGET_STATES.MAXIMIZED, source);
  _entryPointOpenReported = true;
  return true;
}

/**
 * Load the full widget bundle
 */
async function loadFullWidget({ initialMessage, handOffToV1OnFailure }: LoadFullWidgetOptions): Promise<void> {
  try {
    await loadFullWidgetOnce({ initialMessage, handOffToV1OnFailure });
  } finally {
    // Every exit is past the send or past the restore that consumes the text.
    // Anything still parked would pre-fill an unrelated Chat Bar.
    _submittedChatBarText = '';
  }
}

async function loadFullWidgetOnce({
  initialMessage,
  handOffToV1OnFailure = false,
}: LoadFullWidgetOptions): Promise<void> {
  teardownBootstrapUi();

  if (_isWidgetLoaded) {
    logger.log('[bootstrap] Widget already loaded');
    await forwardInitialMessage(initialMessage);
    return;
  }

  // Coalesce concurrent callers onto a single initialization
  if (_widgetLoadPromise) {
    await _widgetLoadPromise;
    // The widget was loaded by a prior caller; forward this caller's message.
    await forwardInitialMessage(initialMessage);
    return;
  }

  // The bootstrap tier is done; its watch must not sit waiting on a mutation
  // that may never come.
  stopSwitchWatch();

  const loadPromise = (async () => {
    logger.log('[bootstrap] Loading full widget...');

    // Ensure mount container exists (e.g. when loading from persisted credentials we never created it)
    createChatContainer();
    // The bootstrap teardown above emptied the host and the mount below has yet
    // to fill it; whatever draws next puts the landmark back.
    setHostLandmark(false);

    // Dynamic import is code-split by Vite: widget (and React) go in a separate chunk,
    // so bootstrap bundle stays React-free (see AGENTS.md). Verify via bundle size / build output.
    const startFullWidgetModule = await import('@/entryPoints/startFullWidget');
    const startFullWidget = startFullWidgetModule.default;

    if (!_initConfig || !_serverConfig || !_dynamicConfig) {
      throw new Error('Configuration not initialized. Call init() first.');
    }

    await startFullWidget({
      clientId: _getClientId(),
      initialMessage,
      initConfig: _initConfig,
      deviceConfig: _deviceConfig,
      serverConfig: _serverConfig,
      dynamicConfig: _dynamicConfig,
      pagePrompts: _pagePrompts,
      testChannel: _testChannelSlot,
      transportDecision: _transportDecision,
      widgetLoadedReported: _bootstrapAnalytics?.hasTrackedWidgetLoaded() ?? false,
    });
    _isWidgetLoaded = true;
    stopDirectLoadWatch();

    // Only once the widget owns analytics. Torn down any earlier and a failed
    // load would restore an entry point whose events go nowhere for the rest of
    // the session; the surviving instance also keeps its one-shot widget-loaded
    // guard, which a replacement would reset.
    _bootstrapAnalytics?.destroy();
    _bootstrapAnalytics = null;
  })();
  // Cleared out here rather than in the body: the body runs synchronously up to
  // its first await, so a throw before that point would clear the field ahead of
  // this assignment and wedge every later load on a settled promise.
  _widgetLoadPromise = loadPromise;

  let failure: unknown = null;
  try {
    await loadPromise;
  } catch (error) {
    failure = error;
  }
  _widgetLoadPromise = null;
  // Released with the load it belongs to, so a failed one does not silence the
  // click that retries it.
  _entryPointOpenReported = false;

  if (failure) {
    logger.error('[bootstrap] Failed to load widget:', failure);
    // The gesture that warmed the connection reached neither claim. A Chat Bar
    // restored with the shopper's text is the exception: it keeps the socket for
    // the retry it is one keystroke away from.
    if (!_submittedChatBarText.trim()) {
      coolBeaconConnection();
    }
    if (isWidgetMounted()) {
      // Past the mount the widget is live and driving the page, whatever the tail
      // of startup did. Latching the flag keeps the next entry point from running
      // startFullWidget a second time over it — a second React root on a wiped
      // shadow root, over a chat gateway re-initialized underneath the running one.
      _isWidgetLoaded = true;
      stopDirectLoadWatch();
      // The widget owns analytics from here, and the entry point this instance
      // served is not coming back; the destroy flushes what it buffered.
      _bootstrapAnalytics?.destroy();
      _bootstrapAnalytics = null;
    } else if (handOffToV1OnFailure) {
      // V1 answers this failure by mounting its own widget: a restored entry point
      // would be a second one, and V1 resolves its host by id so the leftover host
      // would shadow it. The launchers and the Beacon socket sit outside the host
      // and would keep running on their own.
      stopLaunchers();
      stopBeaconPipeline();
      stopPageViewTracking();
      stopOrderCaptureTracking();
      document.getElementById(HOST_CONTAINER_ID)?.remove();
    } else {
      // After the field is cleared — syncBootstrapUi's mid-load guard swallows a
      // restore issued while _widgetLoadPromise is still set.
      _restoreEntryPoint();
    }
    throw failure;
  }
}

// The Chat Bar's resting gap from the viewport bottom, per breakpoint. The
// stylesheet below paints it and the avoidance scan holds the same gap above an
// obstacle. They have to agree: the scan writes an inline `bottom` only when there
// is something to clear, so a divergence would show up as the bar hopping the
// moment a promo bar appears. Keep the breakpoint in sync with MOBILE_QUERY.
const CHAT_BAR_EDGE_OFFSET_PX = 24;
const CHAT_BAR_EDGE_OFFSET_MOBILE_PX = 16;

function createMinimizedButton(appMountElem: HTMLDivElement): void {
  logger.log('[bootstrap] Creating minimized button...');

  // Check if button already exists to prevent duplicate creation
  const existingButton = appMountElem.shadowRoot?.querySelector('#gladlyChatBootstrapInputBackground');
  if (existingButton) {
    logger.log('[bootstrap] Minimized button already exists, skipping creation');
    return;
  }

  // Get existing shadow root or create a new one
  // If shadow root already exists (e.g., from a previous failed load), reuse it
  let shadowRoot = appMountElem.shadowRoot;
  if (!shadowRoot) {
    shadowRoot = appMountElem.attachShadow({ mode: 'open' });
    logger.log('[bootstrap] Shadow root created');
  } else {
    // Clear existing content if shadow root already exists
    shadowRoot.innerHTML = '';
    logger.log('[bootstrap] Reusing existing shadow root');
  }

  // Get chatLauncher color from theme, default to #8C69F0 if not set
  const chatLauncherColor = _serverConfig?.v2?.appearance?.colors?.accent ?? DEFAULT_ON_PAGE_ACCENT;
  // Convert to rgba with 0.9 opacity
  const chatLauncherRgba = colorToRgba(chatLauncherColor, 0.9);

  logger.log('[bootstrap] Using color:', { chatLauncherColor, chatLauncherRgba });

  // Add styles to shadow DOM
  const dropShadow = _serverConfig?.v2?.appearance?.dropShadow ?? false;
  const borderRadiusPx =
    borderRadiusToPx(_serverConfig?.v2?.appearance?.border?.radius) ?? DEFAULT_ON_PAGE_BORDER_RADIUS_PX;
  const fontFamily = _serverConfig?.v2?.appearance?.fonts?.secondary ?? DEFAULT_V2_FONT;
  const style = document.createElement('style');
  style.textContent = `
    ${HOST_RESET_CSS}
    :host {
      font-family: ${fontFamily};
    }
    #gladlyChatBootstrapInputBackground {
      align-items: flex-start;
      background: transparent;
      bottom: ${CHAT_BAR_EDGE_OFFSET_PX}px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      gap: 8px;
      left: 50%;
      max-width: calc(100vw - 32px);
      padding: 5px;
      position: fixed;
      transform: translateX(-50%);
      transition: bottom 0.3s ease, width 0.3s ease;
      width: 410px;
    }
    #gladlyChatPillOuter {
      align-items: center;
      border-radius: 104px;
      border: 3px solid transparent;
      box-sizing: border-box;
      display: flex;
      transition: border-color 0.2s ease;
      width: 100%;
    }
    #gladlyChatBootstrapInput {
      align-items: center;
      background: white;
      border-radius: ${borderRadiusPx};
      border: none;
      box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.08);
      box-sizing: border-box;
      display: flex;
      flex: 1;
      padding: 0px 4px 0 8px;
      position: relative;
    }
    #gladlyChatBootstrapInput::before {
      animation: borderGradientMove 3s ease-in-out infinite;
      background: ${accentGradientStops(chatLauncherColor)};
      background-size: 200% 100%;
      border-radius: calc(${borderRadiusPx} + 6px);
      content: '';
      inset: -6px;
      mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      mask-composite: exclude;
      padding: 6px;
      pointer-events: none;
      position: absolute;
      z-index: -1;
      -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
    }
    @keyframes borderGradientMove {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
    #gladlyChatBootstrapInput input {
      background: transparent;
      border: none;
      color: #000;
      flex: 1;
      font-family: ${fontFamily};
      font-size: 16px;
      outline: none;
      padding: 16px 8px 16px 12px;
    }
    .gladly-input-area {
      display: flex;
      flex: 1;
      position: relative;
    }
    .gladly-placeholder-overlay {
      align-items: center;
      box-sizing: border-box;
      color: #484848;
      display: flex;
      font-size: 16px;
      height: 100%;
      left: 0;
      padding: 0 8px 0 12px;
      pointer-events: none;
      position: absolute;
      top: 0;
      transition: opacity 0.2s ease;
      width: 100%;
    }
    .gladly-placeholder-slider {
      height: 1.15em;
      line-height: 1.15;
      overflow: hidden;
      position: relative;
      width: 100%;
    }
    .gladly-placeholder-text {
      left: 0;
      opacity: 1;
      position: absolute;
      top: 0;
      transform: translateY(0);
      transition: transform 0.4s ease, opacity 0.4s ease;
      white-space: nowrap;
    }
    .gladly-placeholder-text.slide-out {
      opacity: 0;
      transform: translateY(-100%);
    }
    .gladly-placeholder-next {
      left: 0;
      opacity: 0;
      position: absolute;
      top: 0;
      transform: translateY(-100%);
      transition: transform 0.4s ease, opacity 0.4s ease;
      white-space: nowrap;
    }
    .gladly-placeholder-next.slide-in {
      opacity: 1;
      transform: translateY(0);
    }
    #gladlyChatCloseButton:focus-visible {
      outline: 2px solid #005fcc;
      outline-offset: 2px;
    }
    #gladlyChatCloseButton {
      align-items: center;
      background: #FFF;
      border: none;
      border-radius: ${borderRadiusPx};
      box-shadow: ${dropShadow ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none'};
      color: #000;
      cursor: pointer;
      display: flex;
      height: 30px;
      justify-content: center;
      left: calc(100% + 16px);
      opacity: 0;
      padding: 0;
      pointer-events: none;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      transition: opacity 0.15s ease-in-out, background 0.2s ease, color 0.2s ease;
      visibility: hidden;
      width: 30px;
      z-index: 1;
    }
    #gladlyChatCloseButton.visible {
      opacity: 1;
      pointer-events: auto;
      visibility: visible;
    }
    #gladlyChatCloseButton:hover {
      background: ${chatLauncherColor};
      color: #FFF;
    }
    #gladlyChatCloseButton:active {
      transform: translateY(-50%) scale(0.95);
    }
    #gladlyChatCloseButton svg {
      color: inherit;
      cursor: pointer;
      display: block;
      height: 16px;
      width: 16px;
    }
    /* Stack the close button above the input when it can't fit inline:
       mobile breakpoint, or focused on a viewport too narrow to fit it beside. */
    #gladlyChatCloseButton.aboveInput {
      background: ${chatLauncherColor};
      color: #FFF;
      left: auto;
      opacity: 1;
      pointer-events: auto;
      right: 8px;
      top: -32px;
      transform: none;
      visibility: visible;
    }
    #gladlyChatCloseButton.aboveInput:active {
      transform: scale(0.95);
    }
    /* Tooltip styles */
    .gladly-tooltip {
      bottom: 100%;
      filter: drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.25));
      left: 50%;
      margin-bottom: 8px;
      opacity: 0;
      pointer-events: none;
      position: absolute;
      transform: translateX(-50%);
      transition: opacity 0.15s ease-in-out;
      visibility: hidden;
      z-index: 2;
    }
    .gladly-tooltip.visible {
      opacity: 1;
      visibility: visible;
    }
    .gladly-tooltip-content {
      background: #484848;
      border-radius: 8px;
      color: #fff;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 13px;
      font-weight: 400;
      line-height: 162%;
      padding: 5px 8px;
      text-align: center;
      white-space: nowrap;
    }
    .gladly-tooltip-arrow {
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 6px solid #484848;
      height: 0;
      left: 50%;
      position: absolute;
      top: 100%;
      transform: translateX(-50%);
      width: 0;
    }
    /* Keep this breakpoint in sync with MOBILE_QUERY in lib/isMobileWidth.ts */
    @media only screen and (max-width: 500px), screen and (orientation: landscape) and (max-height: 500px) {
      #gladlyChatBootstrapInputBackground {
        bottom: ${CHAT_BAR_EDGE_OFFSET_MOBILE_PX}px;
        left: 16px;
        max-width: none;
        right: 16px;
        transform: none;
        transition: none;
        width: auto;
      }
    }
  `;
  shadowRoot.appendChild(style);

  // Input background container
  const inputBackground = document.createElement('div');
  inputBackground.id = 'gladlyChatBootstrapInputBackground';

  // Outer container (FullChatPill equivalent) - 3px border
  const outerContainer = document.createElement('div');
  outerContainer.id = 'gladlyChatPillOuter';

  // Inner border container (InnerBorder equivalent) - 1px border
  const inputWrapper = document.createElement('div');
  inputWrapper.id = 'gladlyChatBootstrapInput';
  inputWrapper.style.cssText = `
    display: flex;
    flex: 1;
    align-items: center;
    background: white;
    border: none;
    border-radius: ${borderRadiusPx};
    box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.08);
    padding: 0px 4px 0 8px;
    position: relative;
    box-sizing: border-box;
  `;

  // Inner text input (ChatInput equivalent)
  const inputFontFamily = _serverConfig?.v2?.appearance?.fonts?.secondary ?? DEFAULT_V2_FONT;
  const input = document.createElement('input');
  input.type = 'text';
  input.setAttribute('aria-label', 'Ask anything');
  // Before the send button, placeholder and analytics latch below read the input.
  input.value = _submittedChatBarText;
  _submittedChatBarText = '';
  if (input.value.trim()) {
    // Text here is a submit already made; the keystroke latch starts fired.
    warmBeaconConnection();
  }
  input.style.cssText = `
    flex: 1;
    border: none;
    background: transparent;
    color: #000;
    font-family: ${inputFontFamily};
    font-size: 16px;
    padding: 16px 8px 16px 12px;
  `;

  // Function to update width based on focus state and viewport
  const updateWidth = (isFocused: boolean) => {
    let stackCloseButton = false;
    if (isMobileWidth()) {
      // Insets/width come from the @media block; setting them inline would
      // persist on desktop and leave the bar stuck. Stale width is cleared in
      // syncMobileCloseButton.
      outerContainer.style.borderColor = 'transparent';
      stackCloseButton = true;
    } else if (isFocused) {
      const maxWidth = Math.min(832, window.innerWidth - 32);
      inputBackground.style.width = `${maxWidth}px`;
      outerContainer.style.borderColor = 'transparent';
      // Close button needs 16px gap + 30px width = 46px beyond the container's right edge.
      // Container is centered, so it fits when innerWidth >= containerWidth + 2 * 46.
      stackCloseButton = window.innerWidth < maxWidth + 92;
    } else {
      inputBackground.style.width = '410px';
      outerContainer.style.borderColor = 'transparent';
    }
    closeButton.classList.toggle('aboveInput', stackCloseButton);
  };

  // Handle window resize when input is focused
  let resizeHandler: (() => void) | null = null;
  let isSubmitting = false;
  let isClosing = false;
  let currentBootstrapState: WidgetState = WIDGET_STATES.DEFAULT;

  // Focus state - expand background container to 832px (or viewport - 32px), update borders
  input.addEventListener('focus', () => {
    if (isSubmitting) return;
    _bootstrapAnalytics?.trackWidgetInputFocused(WIDGET_STATES.ACTIVE);
    if (currentBootstrapState === WIDGET_STATES.DEFAULT) {
      _bootstrapAnalytics?.trackWidgetViewChanged(WIDGET_STATES.DEFAULT, WIDGET_STATES.ACTIVE);
      currentBootstrapState = WIDGET_STATES.ACTIVE;
    }
    updateWidth(true);
    // Add resize listener when focused
    resizeHandler = () => {
      if (document.activeElement === input) {
        updateWidth(true);
      }
    };
    window.addEventListener('resize', resizeHandler);
  });

  input.addEventListener('blur', () => {
    if (isSubmitting || isClosing) return;
    if (currentBootstrapState === WIDGET_STATES.ACTIVE) {
      _bootstrapAnalytics?.trackWidgetViewChanged(WIDGET_STATES.ACTIVE, WIDGET_STATES.DEFAULT);
      currentBootstrapState = WIDGET_STATES.DEFAULT;
    }
    updateWidth(false);

    // Remove resize listener when blurred
    if (resizeHandler) {
      window.removeEventListener('resize', resizeHandler);
      resizeHandler = null;
    }
  });

  // Paper airplane icon container (submit button)
  const submitIconContainer = document.createElement('div');
  submitIconContainer.id = 'gladlyChatBootstrapSendButton';
  submitIconContainer.style.cssText = `
    flex-shrink: 0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 42px;
    height: 42px;
    box-sizing: border-box;
    cursor: pointer;
    transition: background-color 0.2s;
  `;

  // Paper airplane icon SVG
  const submitIcon = createPaperAirplaneIcon();
  submitIcon.style.cssText = `
    width: 16px;
    height: 16px;
    display: block;
    color: #767676;
    transition: color 0.2s;
  `;

  // Create send button tooltip early so we can control it
  const sendTooltip = createTooltip(submitIconContainer, _serverConfig?.v2?.tooltips?.sendIcon ?? 'Send');

  // Track if send button is enabled (has text)
  const updateSendButtonState = () => {
    const hasText = input.value.trim().length > 0;
    if (hasText) {
      submitIconContainer.style.backgroundColor = chatLauncherColor;
      submitIcon.style.color = '#FFF';
      submitIcon.style.opacity = '1';
      submitIconContainer.style.cursor = 'pointer';
      sendTooltip.setEnabled(true);
    } else {
      submitIconContainer.style.backgroundColor = 'transparent';
      submitIcon.style.color = '#767676';
      submitIcon.style.opacity = '0.5';
      submitIconContainer.style.cursor = 'not-allowed';
      sendTooltip.setEnabled(false);
    }
  };

  // Update button state and track first message typed when input changes. Both
  // latches start fired on a bar restored with the shopper's text. Separate
  // latches on purpose: one exists for an analytics event, the other decides
  // whether a tool call has a socket to arrive on.
  let hasTrackedFirstMessage = input.value.trim().length > 0;
  let hasWarmedBeacon = input.value.trim().length > 0;
  input.addEventListener('input', () => {
    updateSendButtonState();
    const hasText = input.value.trim().length > 0;
    if (!hasTrackedFirstMessage && hasText) {
      hasTrackedFirstMessage = true;
      _bootstrapAnalytics?.trackFirstMessageTyped(WIDGET_STATES.ACTIVE);
    }
    if (!hasWarmedBeacon && hasText) {
      hasWarmedBeacon = true;
      // Submit starts the chunk load and the send together; composing is the
      // only slack this path has.
      warmBeaconConnection();
    }
  });

  // Initial state
  updateSendButtonState();

  // Hover/leave just defers to current input state (button is already
  // filled with the theme color when there is text)
  submitIconContainer.addEventListener('mouseenter', () => {
    updateSendButtonState();
  });

  submitIconContainer.addEventListener('mouseleave', () => {
    updateSendButtonState();
  });

  submitIconContainer.appendChild(submitIcon);

  // Handle ENTER key to submit
  const handleSubmit = async () => {
    const value = input.value.trim();
    if (!value) {
      return;
    }

    logger.log('[bootstrap] Input submitted:', value);

    // Set flag to prevent blur handler from triggering collapse animation
    isSubmitting = true;

    reportEntryPointOpen(currentBootstrapState, 'USER_INTERACTION');

    _submittedChatBarText = value;
    try {
      // Autofill and form restore reach here with neither latch above fired.
      warmBeaconConnection();
      await loadFullWidget({ initialMessage: value });
      revealAfterDeliberateOpen();
    } catch {
      // Nothing to undo: loadFullWidget logs, and has already handed the text
      // to the bar it restored in place of the one this closure belongs to.
    }
  };

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSubmit();
    }
  });

  // Also allow clicking the icon container to submit
  submitIconContainer.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    void handleSubmit();
  });

  // Prevent blur when clicking the icon
  submitIconContainer.addEventListener('mousedown', (e) => {
    e.preventDefault();
  });

  // Create close button (XClose icon)
  const closeButton = document.createElement('button');
  closeButton.id = 'gladlyChatCloseButton';
  closeButton.setAttribute('aria-label', _serverConfig?.v2?.tooltips?.closeWidget ?? 'Hide Chat bar');
  closeButton.type = 'button';

  const closeIcon = createXCloseIcon();
  closeButton.appendChild(closeIcon);

  // On mobile, the close button is always visible (there's no hover to reveal
  // it on). Keep the class in sync with the viewport so it resolves correctly
  // on load and when the window crosses the mobile breakpoint.
  const syncMobileCloseButton = () => {
    if (isMobileWidth()) {
      closeButton.classList.add('aboveInput');
      // Drop stale desktop inline width so the @media block owns mobile. Always
      // attached (unlike the focus-only resize handler), so it reflows in any state.
      inputBackground.style.width = '';
    } else if (shadowRoot.activeElement !== input) {
      closeButton.classList.remove('aboveInput');
    }
  };
  syncMobileCloseButton();
  window.addEventListener('resize', syncMobileCloseButton);

  // Track visibility state with timeout to prevent flickering
  let shouldShowCloseButton = false;
  let hideTimeout: ReturnType<typeof setTimeout> | null = null;

  const showCloseButton = () => {
    // Clear any pending hide timeout
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
    shouldShowCloseButton = true;
    // Ensure visibility is set before adding visible class for smooth fade-in
    closeButton.style.visibility = 'visible';
    closeButton.classList.add('visible');
  };

  const doHideCloseButton = () => {
    // On mobile, always keep the close button visible
    if (isMobileWidth()) return;

    // Only hide if input is not focused, close button is not focused, and we're not hovering.
    // Use shadowRoot.activeElement to get the actual focused element inside the shadow DOM
    // (document.activeElement returns the shadow host, not the inner focused element).
    const focused = shadowRoot.activeElement;
    if (focused !== input && focused !== closeButton && !shouldShowCloseButton) {
      // Remove visible class to trigger fade-out
      closeButton.classList.remove('visible');
      // Wait for transition to complete before hiding visibility
      setTimeout(() => {
        if (!closeButton.classList.contains('visible')) {
          closeButton.style.visibility = 'hidden';
        }
      }, 150); // Match transition duration
    }
  };

  const hideCloseButton = (delay = 0) => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
    if (delay === 0) {
      doHideCloseButton();
    } else {
      hideTimeout = setTimeout(() => {
        doHideCloseButton();
        hideTimeout = null;
      }, delay);
    }
  };

  // Show close button when hovering over input background
  inputBackground.addEventListener('mouseenter', () => {
    showCloseButton();
  });

  inputBackground.addEventListener('mouseleave', (e: MouseEvent) => {
    // Check if mouse is moving to close button
    const relatedTarget = e.relatedTarget;
    if (relatedTarget instanceof Node && closeButton.contains(relatedTarget)) {
      // Mouse is moving to close button, keep it visible
      return;
    }
    // Add a delay before hiding to allow mouse to traverse the gap to the close button
    shouldShowCloseButton = false;
    hideCloseButton(300);
  });

  // Keep close button visible when hovering over it
  closeButton.addEventListener('mouseenter', () => {
    showCloseButton();
  });

  closeButton.addEventListener('mouseleave', () => {
    // Don't hide if button or input is focused
    const focused = shadowRoot.activeElement;
    if (focused !== closeButton && focused !== input) {
      shouldShowCloseButton = false;
      hideCloseButton(300);
    }
  });

  // Show close button when it receives focus (keyboard navigation)
  closeButton.addEventListener('focus', () => {
    showCloseButton();
  });

  closeButton.addEventListener('blur', () => {
    // Don't hide if focus is moving to input
    const focused = shadowRoot.activeElement;
    if (focused !== input) {
      shouldShowCloseButton = false;
      hideCloseButton(0);
    }
  });

  // Show close button when input is focused
  input.addEventListener('focus', () => {
    showCloseButton();
  });

  input.addEventListener('blur', () => {
    // Don't hide if focus is moving to close button
    const focused = shadowRoot.activeElement;
    if (focused !== closeButton) {
      shouldShowCloseButton = false;
      hideCloseButton(0);
    }
  });

  closeButton.addEventListener('pointerdown', () => {
    isClosing = true;
    const handlePointerEnd = () => {
      if (!isClosing) return;
      isClosing = false;
      if (document.activeElement !== input) {
        updateWidth(false);
        if (resizeHandler) {
          window.removeEventListener('resize', resizeHandler);
          resizeHandler = null;
        }
      }
    };
    window.addEventListener('pointerup', handlePointerEnd, { once: true });
    window.addEventListener('pointercancel', handlePointerEnd, { once: true });
  });

  // Handle close button click
  const handleClose = () => {
    logger.log('[bootstrap] Close button clicked');
    _bootstrapAnalytics?.trackWidgetViewChanged(currentBootstrapState, WIDGET_STATES.BUBBLE, 'USER_INTERACTION');
    teardownBootstrapUi();
    coolBeaconConnection();
    // Save bootstrap closed state
    setBootstrapClosedState(true);
    // Show chat icon button
    createChatIconButton(appMountElem);
  };

  closeButton.addEventListener('click', handleClose);

  // Wrap input in inputArea with rotating placeholder overlay
  const inputArea = document.createElement('div');
  inputArea.className = 'gladly-input-area';
  inputArea.appendChild(input);
  const placeholders = resolveChatBarPlaceholders(_serverConfig?.v2);
  const { aiLauncherId } = placeholders;
  function displayedPlaceholders(): DisplayedChatBarPlaceholders {
    return displayedChatBarPlaceholders(placeholders, answerFor(_pagePrompts, aiLauncherId));
  }
  const rotatingPlaceholder = setupRotatingPlaceholder(
    inputArea,
    input,
    inputFontFamily,
    displayedPlaceholders().placeholders,
  );
  // Only an AI bar listens; a static one has nothing to swap in.
  const unsubscribePagePrompts =
    aiLauncherId !== null
      ? _pagePrompts.subscribe(() => {
          const displayed = displayedPlaceholders();
          if (rotatingPlaceholder.replace(displayed.placeholders)) {
            logger.log(
              `[bootstrap] chat bar: rotating ${displayed.placeholders.length} ${displayed.source} placeholders`,
            );
          }
        })
      : null;

  // Assemble the component
  inputWrapper.appendChild(inputArea);
  inputWrapper.appendChild(submitIconContainer);
  outerContainer.appendChild(inputWrapper);
  inputBackground.appendChild(outerContainer);
  inputBackground.appendChild(closeButton);
  shadowRoot.appendChild(inputBackground);

  // Ungated, unlike the chat icon's: renderBootstrapUi takes the icon branch for
  // every Legacy UI org, so the Chat Bar never exists on that skin. Gate this too
  // if it ever does — V1 had no avoidance to inherit. Lazy so the gap tracks the
  // viewport across the breakpoint.
  const obstacleAvoidance = startBottomObstacleAvoidance(inputBackground, () =>
    isMobileWidth() ? CHAT_BAR_EDGE_OFFSET_MOBILE_PX : CHAT_BAR_EDGE_OFFSET_PX,
  );

  registerBootstrapUiTeardown(() => {
    unsubscribePagePrompts?.();
    rotatingPlaceholder.destroy();
    obstacleAvoidance.destroy();
    if (resizeHandler) {
      window.removeEventListener('resize', resizeHandler);
      resizeHandler = null;
    }
    window.removeEventListener('resize', syncMobileCloseButton);
    inputBackground.remove();
  });

  // Add tooltip for close button (send tooltip created earlier)
  createTooltip(closeButton, _serverConfig?.v2?.tooltips?.closeWidget ?? 'Hide Chat bar');

  logger.log('[bootstrap] Minimized button created and appended to shadow root');
  logger.log('[bootstrap] Shadow root children:', shadowRoot.children.length);
}

function setupRotatingPlaceholder(
  inputArea: HTMLDivElement,
  input: HTMLInputElement,
  fontFamily: string,
  initial: PlaceholderList,
): { destroy: () => void; replace: (prompts: PlaceholderList) => boolean } {
  let rotation = initial;
  const overlay = document.createElement('div');
  overlay.className = 'gladly-placeholder-overlay';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.style.fontFamily = fontFamily;

  const slider = document.createElement('div');
  slider.className = 'gladly-placeholder-slider';

  const currentSpan = document.createElement('span');
  currentSpan.className = 'gladly-placeholder-text';
  currentSpan.textContent = rotation[0];

  const nextSpan = document.createElement('span');
  nextSpan.className = 'gladly-placeholder-next';
  nextSpan.textContent = rotation[1 % rotation.length];

  slider.appendChild(currentSpan);
  slider.appendChild(nextSpan);
  overlay.appendChild(slider);
  inputArea.insertBefore(overlay, input);

  // A replacement list waits here for the next slide, which brings its first entry in.
  let pending: PlaceholderList | null = null;
  let currentIndex = 0;
  let sliding = false;
  let destroyed = false;
  let intervalId: ReturnType<typeof setInterval> | undefined;
  let slideInTimeoutId: ReturnType<typeof setTimeout>;
  let resetTimeoutId: ReturnType<typeof setTimeout>;

  // Twin of useRotatingPlaceholder's slide(); a fix to the pending hand-off belongs in both.
  function slide(): void {
    // Captured now: a replacement landing mid-slide must not change what the reset paints.
    const target = pending;
    const list = target ?? rotation;
    const nextIndex = target ? 0 : (currentIndex + 1) % rotation.length;
    sliding = true;
    nextSpan.textContent = list[nextIndex];

    currentSpan.classList.add('slide-out');

    slideInTimeoutId = setTimeout(() => {
      nextSpan.classList.add('slide-in');
    }, 400);

    resetTimeoutId = setTimeout(() => {
      currentSpan.style.transition = 'none';
      nextSpan.style.transition = 'none';

      currentSpan.classList.remove('slide-out');
      currentSpan.textContent = list[nextIndex];

      nextSpan.classList.remove('slide-in');
      nextSpan.textContent = list[(nextIndex + 1) % list.length];

      // Force reflow before re-enabling transitions
      void overlay.offsetHeight;

      currentSpan.style.transition = '';
      nextSpan.style.transition = '';

      currentIndex = nextIndex;
      if (target) {
        rotation = target;
        if (pending === target) pending = null;
      }
      sliding = false;
      // A replacement that arrived during this slide follows straight behind it.
      if (pending) start();
    }, 800);
  }

  // One slide now and the cadence from it; a single prompt has nothing to rotate to, so no interval.
  function start(): void {
    clearInterval(intervalId);
    slide();
    intervalId = (pending ?? rotation).length < 2 ? undefined : setInterval(slide, 4000);
  }

  intervalId = rotation.length < 2 ? undefined : setInterval(slide, 4000);

  const handleInput = () => {
    overlay.style.opacity = input.value.length > 0 ? '0' : '1';
  };

  input.addEventListener('input', handleInput);
  // An input that already carries text must not show the placeholder behind it.
  handleInput();

  return {
    replace(prompts) {
      if (destroyed || samePlaceholders(prompts, pending ?? rotation)) {
        return false;
      }
      pending = prompts;
      // Mid-slide it only waits: clearing the timeouts would strand the text half out.
      if (!sliding) start();
      return true;
    },
    destroy() {
      destroyed = true;
      pending = null;
      clearInterval(intervalId);
      clearTimeout(slideInTimeoutId);
      clearTimeout(resetTimeoutId);
      input.removeEventListener('input', handleInput);
    },
  };
}

function createChatIconButton(appMountElem: HTMLDivElement): void {
  const existingChatIcon = appMountElem.shadowRoot?.querySelector('#gladlyChatIconButton');
  if (existingChatIcon) {
    return;
  }

  // Get existing shadow root or create new one
  let shadowRoot = appMountElem.shadowRoot;
  if (!shadowRoot) {
    shadowRoot = appMountElem.attachShadow({ mode: 'open' });
  }

  // Get chatLauncher color from theme
  const chatLauncherColor = _serverConfig?.v2?.appearance?.colors?.accent ?? DEFAULT_ON_PAGE_ACCENT;
  const chatButtonIconColor = _serverConfig?.v2?.appearance?.colors?.chatButtonIcon ?? '#FFFFFF';
  const borderRadiusPx =
    borderRadiusToPx(_serverConfig?.v2?.appearance?.border?.radius) ?? DEFAULT_ON_PAGE_BORDER_RADIUS_PX;
  const dropShadow = _serverConfig?.v2?.appearance?.dropShadow ?? false;
  const isLeftAligned = _serverConfig?.v2?.appearance?.alignment === ALIGNMENTS.LEFT;
  // Twin of the widget tier's BootstrapChatIcon; both take their face from the
  // same resolver so the button does not change as the widget chunk mounts.
  const { logoUrl, text: buttonText } = resolveLauncherButton(_serverConfig, {
    isExistingUser: getPersistedState()?.user?.id != null,
  });
  const fontFamily = _serverConfig?.v2?.appearance?.fonts?.secondary ?? DEFAULT_V2_FONT;

  // Legacy UI: a plain 50x50 bubble sourced from the legacy V1 config — launcher
  // color, per-config radius, and drop shadow — positioned 15px from the edge,
  // with no animated gradient border ring. Icon color is auto-contrasted.
  const legacyUI = isLegacyUIEnabled(_serverConfig);
  // The gradient ring is dropped in Legacy UI, so only compute it otherwise.
  const gradientBorder = legacyUI ? null : accentGradientStops(chatLauncherColor);
  const legacyTheme = legacyUI && _serverConfig ? getLegacyUITheme(_serverConfig) : null;
  const launcherColor = legacyTheme ? legacyTheme.colors.launcher : chatLauncherColor;
  const iconColor = legacyTheme ? readableColor(legacyTheme.colors.launcher) : chatButtonIconColor;
  const buttonRadius = legacyTheme ? `${legacyTheme.radius.launcher}px` : borderRadiusPx;
  const buttonSize = legacyUI ? '50px' : '56px';
  // The text label's own face. Two things the icon path never needed:
  //   - `primary`, not the `fontFamily` above. That is `fonts.secondary`, while the
  //     widget twin's `.buttonText` reads `--font-primary` — and V1's text button
  //     used `fontLibrary.primary` (web/'s MinimizedChatButton `ButtonContent`). The
  //     pill is sized by its label, so a mismatch resizes the button on mount rather
  //     than merely repainting it.
  //   - set on the element. The UA's own `button { font: ... Arial }` matches the
  //     button directly and so beats the inherited `:host` family — without this the
  //     bootstrap label paints Arial whatever the org configured.
  const labelFontFamily = legacyTheme
    ? legacyTheme.fonts.primary
    : (_serverConfig?.v2?.appearance?.fonts?.primary ?? DEFAULT_V2_FONT);
  // A label is text, so it owes 4.5:1 rather than the 3:1 a glyph owes; derive it
  // from the button's own fill like the widget twin does.
  //
  // The pill is capped at the viewport less both edge gutters, and overruns are
  // handled per skin: Enhanced clips to one line with an ellipsis, Basic wraps,
  // because V1 wrapped (nothing in its launcher chain sets `white-space`). Without
  // a cap, `nowrap` makes shrink-to-fit resolve to the whole label and a fixed
  // right-anchored pill grows off the left edge with no scrollbar to follow it.
  const labelColor = readableColor(launcherColor);
  // One source for the resting offset: the stylesheet paints it and, off the legacy
  // skin, the avoidance scan holds the same gap above an obstacle. Two copies is how
  // the CSS value stopped mattering.
  const buttonEdgeOffsetPx = legacyUI ? 15 : 24;
  const buttonEdgeOffset = `${buttonEdgeOffsetPx}px`;
  // Subtle ring so a light V1 bubble stays visible without a drop shadow.
  const buttonBorder = legacyUI ? '1px solid rgba(0, 0, 0, 0.1)' : 'none';
  const buttonShadow = legacyTheme
    ? legacyTheme.dropshadowEnabled
      ? '0 0 30px 1px rgba(0, 0, 0, 0.2)'
      : 'none'
    : dropShadow
      ? '0 1px 30px 0 rgba(0, 0, 0, 0.2)'
      : 'none';
  const gradientBorderCss = legacyUI
    ? ''
    : `
    #gladlyChatIconButton::before {
      animation: gladlyChatIconGradientMove 3s ease-in-out infinite;
      background: var(--gradient-border);
      background-size: 200% 100%;
      border-radius: calc(${borderRadiusPx} + 6px);
      content: '';
      inset: -6px;
      mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      mask-composite: exclude;
      padding: 6px;
      pointer-events: none;
      position: absolute;
      z-index: -1;
      -webkit-mask:
        linear-gradient(#fff 0 0) content-box,
        linear-gradient(#fff 0 0);
      -webkit-mask-composite: xor;
    }
    @media (prefers-reduced-motion: reduce) {
      #gladlyChatIconButton::before {
        animation: none;
      }
    }
    @keyframes gladlyChatIconGradientMove {
      0% { background-position: 0% 50%; }
      50% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }`;

  // Add styles to shadow DOM
  const style = document.createElement('style');
  style.textContent = `
    ${HOST_RESET_CSS}
    :host {
      font-family: ${fontFamily};
    }
    #gladlyChatIconButton {
      align-items: center;
      background: ${launcherColor};
      border: ${buttonBorder};
      border-radius: ${buttonRadius};
      bottom: ${buttonEdgeOffset};
      box-shadow: ${buttonShadow};
      box-sizing: border-box;
      cursor: pointer;
      display: flex;
      height: ${buttonSize};
      justify-content: center;
      ${isLeftAligned ? `left: ${buttonEdgeOffset};` : `right: ${buttonEdgeOffset};`}
      padding: 0;
      position: fixed;
      transition: bottom 0.3s ease;
      width: ${buttonText ? 'auto' : buttonSize};
      ${buttonText ? `color: ${labelColor}; font-family: ${labelFontFamily}; font-size: 14px; font-weight: 500; line-height: 16px; max-width: calc(100vw - ${buttonEdgeOffsetPx * 2}px); min-width: ${buttonSize}; ${legacyUI ? `height: auto; min-height: ${buttonSize}; overflow-wrap: anywhere; padding: 8px 15px; white-space: normal;` : 'padding: 0 16px; white-space: nowrap;'}` : ''}
    }
    ${gradientBorderCss}
    #gladlyChatIconButton:hover {
      transform: scale(1.05);
    }
    #gladlyChatIconButton:active {
      transform: scale(0.95);
    }
    #gladlyChatIconButton:focus-visible {
      outline: 2px solid #005fcc;
      outline-offset: 2px;
    }
    #gladlyChatIconButton svg {
      color: ${iconColor};
      display: block;
      height: 24px;
      width: 24px;
    }
    #gladlyChatIconButton img {
      display: block;
      height: ${legacyUI ? 28 : 32}px;
      object-fit: contain;
      width: ${legacyUI ? 28 : 32}px;
    }
    ${
      buttonText && !legacyUI
        ? `#gladlyChatIconButton span { min-width: 0; overflow: hidden; text-overflow: ellipsis; }`
        : ''
    }
    .gladly-tooltip {
      bottom: 100%;
      filter: drop-shadow(0px 1px 0px rgba(0, 0, 0, 0.25));
      ${isLeftAligned ? 'left: 0;' : 'left: auto; right: 0;'}
      margin-bottom: 8px;
      opacity: 0;
      pointer-events: none;
      position: absolute;
      transition: opacity 0.15s ease-in-out;
      visibility: hidden;
      z-index: 1;
    }
    .gladly-tooltip.visible {
      opacity: 1;
      visibility: visible;
    }
    .gladly-tooltip-content {
      background: #484848;
      border-radius: 8px;
      color: #fff;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 13px;
      font-weight: 400;
      line-height: 162%;
      max-width: 280px;
      padding: 5px 8px;
      text-align: center;
      overflow-wrap: anywhere;
      white-space: normal;
      width: max-content;
    }
    .gladly-tooltip-arrow {
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 6px solid #484848;
      height: 0;
      ${isLeftAligned ? 'left: 24px;' : 'left: auto; right: 24px;'}
      position: absolute;
      top: 100%;
      width: 0;
    }
    #gladlyChatIconButton:hover .gladly-tooltip {
      transform: scale(0.952);
      transform-origin: ${isLeftAligned ? 'left bottom' : 'right bottom'};
    }
  `;
  shadowRoot.appendChild(style);

  // Create chat icon button
  const chatIconButton = document.createElement('button');
  chatIconButton.id = 'gladlyChatIconButton';
  // Always labelled on this tier, unlike the widget twin. `createTooltip` appends the
  // tooltip INTO this button, so leaving the name to its contents lets the tooltip's
  // copy join it the moment it becomes visible — the accessible name would change
  // under the pointer and the tooltip text be announced twice, once in the name and
  // once through aria-describedby. V1 labelled it too (MinimizedChatButton.jsx).
  chatIconButton.setAttribute('aria-label', buttonText || 'Open chat');
  chatIconButton.type = 'button';
  if (!legacyUI) {
    chatIconButton.style.setProperty('--gradient-border', gradientBorder);
  }

  const buildChatIconSvg = (): SVGSVGElement => {
    const chatIconSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    chatIconSvg.setAttribute('width', '24');
    chatIconSvg.setAttribute('height', '24');
    chatIconSvg.setAttribute('viewBox', '0 0 32 32');
    chatIconSvg.setAttribute('fill', 'none');
    chatIconSvg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');

    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute(
      'd',
      'M29.333 0C30.8058 0 32 1.19423 32 2.66699V21.665C31.9998 23.1376 30.8057 24.3311 29.333 24.3311H17.9014L7.66797 31.9131C7.3743 32.1306 6.95824 31.9201 6.95898 31.5547L6.97852 24.3311H2.66699C1.19435 24.3311 0.000185262 23.1376 0 21.665V2.66699C0 1.19423 1.19423 0 2.66699 0H29.333ZM8.42871 15.7246C7.8766 15.7248 7.42877 16.1643 7.42871 16.707C7.42871 17.2498 7.87656 17.6903 8.42871 17.6904H19.5723C20.1244 17.6902 20.5723 17.2498 20.5723 16.707C20.5722 16.1643 20.1243 15.7248 19.5723 15.7246H8.42871ZM8.57031 11.7979C8.01807 11.7979 7.57038 12.2374 7.57031 12.7803C7.57031 13.3232 8.01803 13.7637 8.57031 13.7637H23.4268C23.979 13.7637 24.4268 13.3232 24.4268 12.7803C24.4267 12.2374 23.979 11.7979 23.4268 11.7979H8.57031ZM8.57031 7.86328C8.01807 7.86328 7.57038 8.30285 7.57031 8.8457C7.57031 9.38861 8.01803 9.8291 8.57031 9.8291H23.4268C23.979 9.8291 24.4268 9.38861 24.4268 8.8457C24.4267 8.30285 23.979 7.86328 23.4268 7.86328H8.57031Z',
    );
    path.setAttribute('fill', iconColor);
    chatIconSvg.appendChild(path);
    return chatIconSvg;
  };

  // Face the button: the configured label, else the header logo, else the Chat
  // glyph. Which of the three is settled by resolveLauncherButton above, shared
  // with the widget tier; a logo that fails to load still falls back here.
  if (buttonText) {
    // A span rather than a bare text node: the ellipsis needs an element to apply
    // to, and a text node in a flex container is an anonymous item that cannot be
    // styled. Also keeps this tier structurally identical to the widget twin's
    // `.buttonText`. The tooltip is a sibling div, so the `span` selector above
    // reaches only the label.
    const label = document.createElement('span');
    label.textContent = buttonText;
    chatIconButton.appendChild(label);
  } else if (logoUrl) {
    const logoImg = document.createElement('img');
    logoImg.src = logoUrl;
    logoImg.alt = '';
    logoImg.onerror = () => {
      logoImg.replaceWith(buildChatIconSvg());
    };
    chatIconButton.appendChild(logoImg);
  } else {
    chatIconButton.appendChild(buildChatIconSvg());
  }

  shadowRoot.appendChild(chatIconButton);

  // Legacy UI sits where the V1 widget sat: pinned to its corner, never nudged up
  // over a fixed footer or promo bar. V1 had no such avoidance to inherit.
  const obstacleAvoidance = legacyUI ? null : startBottomObstacleAvoidance(chatIconButton, buttonEdgeOffsetPx);

  registerBootstrapUiTeardown(() => {
    obstacleAvoidance?.destroy();
    chatIconButton.remove();
  });

  // Handle click to load full widget
  const handleChatIconClick = async () => {
    logger.log('[bootstrap] Chat icon clicked, loading full widget');
    reportEntryPointOpen(WIDGET_STATES.BUBBLE, 'USER_INTERACTION');
    try {
      warmBeaconConnection();
      await loadFullWidget({});
      revealAfterDeliberateOpen();
    } catch {
      // loadFullWidget logs and restores; caught only so the fire-and-forget
      // click doesn't reject unhandled.
    }
  };

  chatIconButton.addEventListener('click', () => {
    void handleChatIconClick();
  });

  const tooltipText = _serverConfig?.v2?.tooltips?.chatButton ?? '';
  if (tooltipText) {
    createTooltip(chatIconButton, tooltipText);
  }
}

/**
 * Continuously adjust an element's `bottom` style to avoid overlapping
 * fixed/sticky bars at the viewport bottom. Scans run on scroll, resize,
 * and a 1-second polling interval (to catch bars toggled by JavaScript
 * without user interaction).
 *
 * With nothing to avoid the inline `bottom` is cleared rather than set to
 * `baseBottom`, so the element rests wherever the stylesheet puts it. Writing the
 * base unconditionally is what made the Legacy UI launcher's 15px edge offset
 * unreachable: the base is never 0, so every pass pinned the element at 24px and
 * the stylesheet never got a say. That also left `baseBottom` duplicating a value
 * the CSS already owns, where changing one silently did nothing.
 *
 * @param element - The positioned element whose `bottom` should be adjusted.
 * @param baseBottom - The gap to hold above an obstacle, in pixels. Match the
 *   stylesheet's resting offset so clearing the inline value doesn't shift the element.
 * @returns A handle with a `destroy()` method that removes all listeners and clears the interval.
 */
function startBottomObstacleAvoidance(
  element: HTMLElement,
  baseBottom: number | (() => number),
): { destroy: () => void } {
  const scan = throttle(() => {
    const height = computeBottomObstacleHeight(element);
    // baseBottom may be a getter so the gap can track the viewport (16/24px).
    const base = typeof baseBottom === 'function' ? baseBottom() : baseBottom;
    element.style.bottom = height > 0 ? `${base + height}px` : '';
  }, 200);

  scan();
  window.addEventListener('scroll', scan, { passive: true });
  window.addEventListener('resize', scan, { passive: true });
  const intervalId = setInterval(scan, 1000);

  return {
    destroy() {
      scan.cancel();
      window.removeEventListener('scroll', scan);
      window.removeEventListener('resize', scan);
      clearInterval(intervalId);
    },
  };
}

/**
 * Scan the host document for fixed/sticky elements near the viewport bottom
 * that horizontally overlap the given widget element, and return the distance
 * from the tallest one's top edge to the viewport bottom (or 0 if none).
 *
 * Uses `viewportBottom - rect.top` instead of `rect.height` so bars with a
 * non-zero CSS `bottom` offset (e.g. `bottom: 20px`) produce an offset that
 * covers both the bar and the gap beneath it.
 */
function computeBottomObstacleHeight(widgetEl: HTMLElement): number {
  let maxHeight = 0;
  const viewportBottom = window.innerHeight;
  const widgetRect = widgetEl.getBoundingClientRect();

  const elements = document.body.querySelectorAll('*');
  for (const el of elements) {
    if (!(el instanceof HTMLElement)) continue;

    const style = getComputedStyle(el);
    if (style.position !== 'fixed' && style.position !== 'sticky') continue;
    if (style.display === 'none' || style.visibility === 'hidden') continue;

    const rect = el.getBoundingClientRect();
    if (rect.height === 0) continue;
    if (rect.height > viewportBottom / 2) continue;
    if (rect.bottom <= viewportBottom - 200) continue;

    const horizontalOverlap = rect.left < widgetRect.right && rect.right > widgetRect.left;
    if (!horizontalOverlap) continue;

    maxHeight = Math.max(maxHeight, viewportBottom - rect.top);
  }

  return maxHeight;
}

/**
 * Read user credentials from persisted state.
 * Uses shared getPersistedState() to read from localStorage with expiration check.
 */
function getPersistedUserCredentials(): { userId?: string; token?: string } | null {
  const state = getPersistedState();
  const user = state?.user;

  if (user?.userId && user?.token) {
    return { userId: user.userId, token: user.token };
  }

  return null;
}

// Export the Gladly object (similar to how V1 does it)
// This will be the default export that gets assigned to window.Gladly
const GladlyAPI = {
  _connectTestChannel,
  _enterTestMode,
  _exitTestMode,
  _getClientId,
  _getEmitter,
  _getTestModeStatus,
  _getTestModeSupport,
  // Exposed for the separately-shipped V1 bundle, whose fallback would otherwise
  // mount a second widget on top of this one after a post-mount init failure.
  _isWidgetMounted: isWidgetMounted,
  _replayPendingToolStatus,
  _restartTestConversation,
  _setActionEnvironment,
  _setStore,
  _showLogging,
  version: WIDGET_VERSION,
  applyCampaign,
  clearUser,
  close,
  getAvailability,
  getInfo,
  getStatus,
  getUser,
  init,
  navigate,
  on,
  reconnect,
  setUser,
  show,
  showGreeting,
  skipOnboarding,
  startConversation,
};

// Replace window.Gladly synchronously when this module runs so there is no tick where
// V1's Gladly is still visible and a caller could invoke a method before replacement.
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  (window as WindowWithGladly).Gladly = GladlyAPI;
}

export default GladlyAPI;
